import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import './App.css';

// API base URL for backend calls
const API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://creatorcents.app/api'
  : '/api';

// Address Autocomplete Component using backend API proxy
function AddressAutocomplete({ value, onChange, placeholder, disabled, className }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions from backend API
  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/place-autocomplete?input=${encodeURIComponent(input)}`);
      const data = await response.json();

      if (data.predictions && data.predictions.length > 0) {
        setSuggestions(data.predictions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (e) {
      console.error('Autocomplete error:', e);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (newValue.length >= 3) {
      setIsLoading(true);
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(newValue);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    const address = suggestion.description;
    setInputValue(address);
    onChange(address);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div ref={containerRef} className="address-autocomplete-container">
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        autoComplete="off"
      />
      {showSuggestions && suggestions.length > 0 && (
        <ul className="address-suggestions">
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.placeId || index}
              onClick={() => handleSelectSuggestion(suggestion)}
              className="address-suggestion-item"
            >
              <span className="suggestion-main">{suggestion.mainText}</span>
              {suggestion.secondaryText && (
                <span className="suggestion-secondary">{suggestion.secondaryText}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {isLoading && <div className="address-loading">...</div>}
    </div>
  );
}

// Register Service Worker for PWA support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(registration => {
        console.log('SW registered:', registration.scope);
      })
      .catch(error => {
        console.log('SW registration failed:', error);
      });
  });
}

const CATEGORIES = [
  { id: 'income', label: 'Income', icon: '💰', affectsTotals: true, isIncome: true, color: '#10b981' },
  { id: 'expense', label: 'Business Expense', icon: '💼', affectsTotals: true, isIncome: false, color: '#8b5cf6' },
  { id: 'commission', label: 'Commission & Fees', icon: '💸', affectsTotals: true, isIncome: false, color: '#f59e0b' },
  { id: 'miles', label: 'Miles Driven', icon: '🚗', affectsTotals: false, isIncome: false, color: '#6366f1' },
  { id: 'fuel', label: 'Fuel', icon: '⛽', affectsTotals: true, isIncome: false, color: '#ef4444' },
  { id: 'tolls', label: 'Tolls', icon: '🛣️', affectsTotals: true, isIncome: false, color: '#ec4899' },
  { id: 'food', label: 'Food', icon: '🍽️', affectsTotals: true, isIncome: false, color: '#14b8a6' },
  { id: 'lodging', label: 'Lodging', icon: '🏨', affectsTotals: true, isIncome: false, color: '#f97316' },
  { id: 'flights', label: 'Flights', icon: '✈️', affectsTotals: true, isIncome: false, color: '#3b82f6' }
];

// Subcategories for Business Expense
const EXPENSE_SUBCATEGORIES = [
  { id: 'supplies', label: 'Supplies' },
  { id: 'advertising', label: 'Advertising' },
  { id: 'office', label: 'Office Expense' },
  { id: 'software', label: 'Software' },
  { id: 'contract_labor', label: 'Contract Labor' },
  { id: 'other', label: 'Other' }
];

// Demo data for try-before-signup experience
const DEMO_DATA = [
  { id: 'demo-1', category: 'income', amount: 2500.00, description: 'YouTube AdSense - January', date: '2026-01-15', receiptPath: null, receiptName: '', subcategory: null },
  { id: 'demo-2', category: 'income', amount: 1200.00, description: 'Brand Deal - TechGadget Co', date: '2026-01-10', receiptPath: null, receiptName: '', subcategory: null },
  { id: 'demo-3', category: 'expense', amount: 89.99, description: 'Adobe Creative Cloud subscription', date: '2026-01-05', receiptPath: null, receiptName: '', subcategory: 'software' },
  { id: 'demo-4', category: 'commission', amount: 125.00, description: 'Platform fees - Patreon', date: '2026-01-08', receiptPath: null, receiptName: '', subcategory: null },
  { id: 'demo-5', category: 'miles', amount: 45.2, description: 'Drive to studio for filming', date: '2026-01-12', receiptPath: null, receiptName: '', subcategory: null },
  { id: 'demo-6', category: 'food', amount: 32.50, description: 'Client lunch meeting', date: '2026-01-14', receiptPath: null, receiptName: '', subcategory: null },
  { id: 'demo-7', category: 'expense', amount: 299.00, description: 'Ring light and microphone', date: '2026-01-03', receiptPath: null, receiptName: '', subcategory: 'supplies' },
  { id: 'demo-8', category: 'fuel', amount: 18.75, description: 'Gas station fill-up', date: '2026-01-11', receiptPath: null, receiptName: '', subcategory: null },
  { id: 'demo-9', category: 'income', amount: 450.00, description: 'Affiliate commission - Amazon', date: '2026-01-18', receiptPath: null, receiptName: '', subcategory: null },
  { id: 'demo-10', category: 'tolls', amount: 12.50, description: 'Highway tolls - event coverage', date: '2026-01-09', receiptPath: null, receiptName: '', subcategory: null }
];

// Backward compatibility: map old category IDs to new ones
const CATEGORY_ALIASES = {
  'charging': 'fuel'
};

function normalizeCategory(categoryId) {
  return CATEGORY_ALIASES[categoryId] || categoryId;
}

// Privacy Policy Component
function PrivacyPolicy({ onBack }) {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <button onClick={onBack} className="legal-back-button">← Back to Home</button>
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: January 19, 2026</p>

        <div className="legal-content">
          <p>This Privacy Notice for CreatorCents ("we," "us," or "our"), describes how and why we might access, collect, store, use, and/or share ("process") your personal information when you use our services ("Services").</p>

          <h2>1. What Information Do We Collect?</h2>
          <p>We collect personal information that you voluntarily provide to us when you register on the Services. The personal information we collect may include email addresses and passwords.</p>
          <p><strong>Payment Data.</strong> All payment data is handled and stored by Stripe. You may find their privacy notice at <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a>.</p>

          <h2>2. How Do We Process Your Information?</h2>
          <p>We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.</p>

          <h2>3. How Long Do We Keep Your Information?</h2>
          <p>We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.</p>

          <h2>4. How Do We Keep Your Information Safe?</h2>
          <p>We have implemented appropriate technical and organizational security measures. However, no electronic transmission over the Internet can be guaranteed to be 100% secure.</p>

          <h2>5. What Are Your Privacy Rights?</h2>
          <p>You may review, change, or terminate your account at any time by logging into your account settings or contacting us.</p>

          <h2>6. Contact Us</h2>
          <p>If you have questions, email us at <a href="mailto:support@creatorcents.app">support@creatorcents.app</a></p>
        </div>
      </div>
    </div>
  );
}

// Terms of Service Component
function TermsOfService({ onBack }) {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <button onClick={onBack} className="legal-back-button">← Back to Home</button>
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: January 2025</p>

        <div className="legal-content">
          <h2>1. Service Description</h2>
          <p>CreatorCents is an expense and income tracking application. The service is provided "as-is" without warranties.</p>

          <h2>2. User Responsibilities</h2>
          <p>You are solely responsible for the accuracy of all data you enter. Consult with a qualified tax professional for tax-related decisions.</p>

          <h2>3. Subscription and Cancellation</h2>
          <p>You may cancel your subscription at any time. Upon cancellation, you will retain access until the end of your current billing period.</p>

          <h2>4. Limitation of Liability</h2>
          <p>CreatorCents shall not be liable for any indirect, incidental, special, consequential, or punitive damages.</p>

          <h2>5. Contact</h2>
          <p>For questions, contact us at: <a href="mailto:support@creatorcents.app">support@creatorcents.app</a></p>
        </div>
      </div>
    </div>
  );
}

// Landing Page Component
function LandingPage({ onGetStarted, onLogin, onTryDemo, onPrivacy, onTerms }) {
  return (
    <div className="landing-page">
      <header className="landing-header">
        <div className="landing-logo">CreatorCents</div>
        <button onClick={onLogin} className="landing-login-button">Log In</button>
      </header>

      <section className="hero-section">
        <h1 className="hero-title">Simple Expense Tracking for Creators</h1>
        <p className="hero-subtitle">
          Track your income, expenses, and mileage all in one place.
          Stay organized for tax season without the spreadsheet headaches.
        </p>
        <div className="hero-buttons">
          <button onClick={onGetStarted} className="hero-cta">Get Started Free</button>
          <button onClick={onTryDemo} className="hero-cta-secondary">Try Demo</button>
        </div>
        <p className="hero-login-link">
          Already have an account? <button onClick={onLogin} className="text-link">Log in</button>
        </p>
      </section>

      <section className="features-section">
        <h2 className="section-title">Everything You Need</h2>
        <div className="features-grid">
          <div className="feature-card">
            <span className="feature-icon">📊</span>
            <h3>9 Expense Categories</h3>
            <p>Income, expenses, commissions, miles, fuel, tolls, food, lodging, and flights</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">📎</span>
            <h3>Receipt Uploads</h3>
            <p>Attach photos of receipts to any expense for easy record keeping</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">🚗</span>
            <h3>Mileage Tracking</h3>
            <p>Log business miles separately for accurate tax deductions</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">📥</span>
            <h3>CSV Export</h3>
            <p>Export your data anytime for your accountant or tax software</p>
          </div>
        </div>
      </section>

      <section className="pricing-section">
        <h2 className="section-title">Choose Your Plan</h2>
        <p className="pricing-subtitle">Start tracking today with AI-powered receipt scanning</p>
        <p className="pricing-slogan">All for less than 2 coffees a month ☕</p>
        <div className="pricing-cards pricing-cards-wide">
          <div className="pricing-card">
            <div className="pricing-tier">Basic</div>
            <div className="pricing-amount">$1.99</div>
            <div className="pricing-period">per month</div>
            <div className="pricing-savings">or $14.99/year (save 37%)</div>
            <ul className="pricing-features">
              <li>Unlimited manual entries</li>
              <li>10 AI receipt scans/month</li>
              <li>Receipt uploads</li>
              <li>CSV export</li>
              <li>All 9 categories</li>
            </ul>
            <button onClick={onGetStarted} className="pricing-cta secondary">Start Basic</button>
          </div>
          <div className="pricing-card featured">
            <div className="pricing-badge">Best Value</div>
            <div className="pricing-tier">Pro</div>
            <div className="pricing-amount">$3.99</div>
            <div className="pricing-period">per month</div>
            <div className="pricing-savings">or $29.99/year (save 37%)</div>
            <ul className="pricing-features">
              <li>Unlimited manual entries</li>
              <li>Unlimited AI receipt scans</li>
              <li>Receipt uploads</li>
              <li>CSV export</li>
              <li>All 9 categories</li>
              <li>Priority support</li>
            </ul>
            <button onClick={onGetStarted} className="pricing-cta">Start Pro</button>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <p>Built for creators, by creators</p>
        <div className="footer-links">
          <button onClick={onPrivacy} className="footer-link">Privacy Policy</button>
          <span className="footer-divider">|</span>
          <button onClick={onTerms} className="footer-link">Terms of Service</button>
        </div>
      </footer>
    </div>
  );
}

// Auth Component
function Auth({ onBack, defaultIsLogin = true }) {
  const [isLogin, setIsLogin] = useState(defaultIsLogin);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Load saved email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('creatorcents_remembered_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isLogin) {
        // Save or remove email based on remember me
        if (rememberMe) {
          localStorage.setItem('creatorcents_remembered_email', email);
        } else {
          localStorage.removeItem('creatorcents_remembered_email');
        }
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        alert('Check your email for a confirmation link!');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password'
      });
      if (error) throw error;
      setResetEmailSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h1 className="auth-title">CreatorCents</h1>
          <p className="auth-subtitle">Expense & Income Tracker</p>

          {resetEmailSent ? (
            <div className="auth-form">
              <h2>Check Your Email</h2>
              <p className="auth-success-message">
                We've sent password reset instructions to <strong>{email}</strong>
              </p>
              <p className="auth-success-hint">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <button
                onClick={() => { setShowForgotPassword(false); setResetEmailSent(false); }}
                className="auth-button"
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="auth-form">
              <h2>Reset Password</h2>
              <p className="auth-hint">Enter your email and we'll send you reset instructions.</p>

              {error && <div className="auth-error">{error}</div>}

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <button type="submit" className="auth-button" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          <button
            onClick={() => { setShowForgotPassword(false); setError(null); }}
            className="auth-back-button"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">CreatorCents</h1>
        <p className="auth-subtitle">Expense & Income Tracker</p>

        <form onSubmit={handleSubmit} className="auth-form">
          <h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>

          {error && <div className="auth-error">{error}</div>}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />
            {isLogin && (
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="forgot-password-link"
              >
                Forgot password?
              </button>
            )}
          </div>

          {isLogin && (
            <div className="remember-me-group">
              <label className="remember-me-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
            </div>
          )}

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? 'Loading...' : (isLogin ? 'Sign In' : 'Sign Up')}
          </button>
        </form>

        <p className="auth-toggle">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button onClick={() => setIsLogin(!isLogin)} className="auth-toggle-button">
            {isLogin ? 'Sign Up' : 'Sign In'}
          </button>
        </p>

        {onBack && (
          <button onClick={onBack} className="auth-back-button">Back to Home</button>
        )}
      </div>
    </div>
  );
}

// Paywall Component
function Paywall({ user, onSubscribed }) {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState(null);

  const handleSubscribe = async (plan) => {
    setLoading(true);
    setSelectedPlan(plan);
    setError(null);

    try {
      const response = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, email: user.email, plan })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create checkout session');

      if (data.updated) {
        // Subscription was updated directly (existing customer)
        console.log('Subscription updated successfully:', data.subscriptionId);
        // Trigger subscription refresh by calling onSubscribed
        onSubscribed();
        alert('Your plan has been updated successfully!');
      } else if (data.url) {
        // New subscription - redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message);
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      // Clear all Supabase-related localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') || key.startsWith('supabase')) {
          localStorage.removeItem(key);
        }
      });
      sessionStorage.clear();
      window.location.href = '/';
    } catch (err) {
      console.error('Sign out error:', err);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    }
  };

  return (
    <div className="paywall-container">
      <div className="paywall-content">
        <h1 className="paywall-title">CreatorCents</h1>
        <p className="paywall-subtitle">Choose Your Plan</p>

        {error && <div className="paywall-error">{error}</div>}

        {/* Basic Plan Section */}
        <div className="plan-section">
          <h2 className="plan-section-title">Basic</h2>
          <p className="plan-section-desc">Manual entry + 10 AI scans/month</p>

          <div className="plan-options-grid">
            {/* Basic Monthly - LEFT */}
            <div className="plan-option" onClick={() => !loading && handleSubscribe('basic_monthly')}>
              <div className="plan-price">$1.99</div>
              <div className="plan-period">per month</div>
              <div className="plan-total">$23.88/year</div>
              <button className="plan-button outline" disabled={loading}>
                {loading && selectedPlan === 'basic_monthly' ? 'Loading...' : 'Choose Monthly'}
              </button>
            </div>

            {/* Basic Yearly - RIGHT */}
            <div className="plan-option" onClick={() => !loading && handleSubscribe('basic_yearly')}>
              <div className="plan-price">$14.99</div>
              <div className="plan-period">per year</div>
              <div className="plan-savings">Save 37%</div>
              <button className="plan-button primary" disabled={loading}>
                {loading && selectedPlan === 'basic_yearly' ? 'Loading...' : 'Choose Yearly'}
              </button>
            </div>
          </div>

          {/* Basic Features */}
          <div className="plan-features">
            <div className="plan-feature"><span className="feature-check">✓</span> Unlimited manual entries</div>
            <div className="plan-feature"><span className="feature-check">✓</span> 10 AI receipt scans/month</div>
            <div className="plan-feature"><span className="feature-check">✓</span> Receipt uploads</div>
            <div className="plan-feature"><span className="feature-check">✓</span> CSV export</div>
          </div>
        </div>

        {/* Pro Plan Section */}
        <div className="plan-section plan-section-pro">
          <h2 className="plan-section-title">Pro</h2>
          <p className="plan-section-desc">Unlimited AI receipt scanning</p>

          <div className="plan-options-grid">
            {/* Pro Monthly - LEFT */}
            <div className="plan-option" onClick={() => !loading && handleSubscribe('pro_monthly')}>
              <div className="plan-price">$3.99</div>
              <div className="plan-period">per month</div>
              <div className="plan-total">$47.88/year</div>
              <button className="plan-button outline" disabled={loading}>
                {loading && selectedPlan === 'pro_monthly' ? 'Loading...' : 'Choose Monthly'}
              </button>
            </div>

            {/* Pro Yearly - RIGHT - Best Value */}
            <div className="plan-option plan-option-featured" onClick={() => !loading && handleSubscribe('pro_yearly')}>
              <div className="plan-badge">BEST VALUE</div>
              <div className="plan-price">$29.99</div>
              <div className="plan-period">per year</div>
              <div className="plan-savings">Save 37%</div>
              <button className="plan-button primary" disabled={loading}>
                {loading && selectedPlan === 'pro_yearly' ? 'Loading...' : 'Choose Yearly'}
              </button>
            </div>
          </div>

          {/* Pro Features */}
          <div className="plan-features">
            <div className="plan-feature"><span className="feature-check">✓</span> Unlimited manual entries</div>
            <div className="plan-feature"><span className="feature-check">✓</span> Unlimited AI receipt scans</div>
            <div className="plan-feature"><span className="feature-check">✓</span> Receipt uploads</div>
            <div className="plan-feature"><span className="feature-check">✓</span> CSV export</div>
            <div className="plan-feature"><span className="feature-check">✓</span> Priority support</div>
          </div>
        </div>

        <p className="paywall-footer">
          Logged in as {user.email}
          <button onClick={handleSignOut} className="paywall-signout">Sign Out</button>
        </p>
      </div>
    </div>
  );
}

// Side Navigation Component
function SideNav({ activeView, setActiveView, user, subscription, isDemo, onExitDemo, onSignOut, isMobileOpen, setIsMobileOpen }) {
  const isPro = subscription?.plan === 'pro_monthly' || subscription?.plan === 'pro_yearly';

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'reports', label: 'Cash Flow', icon: '📈' },
    { id: 'receipts', label: 'Receipts', icon: '🧾' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    { id: 'help', label: 'Help', icon: '❓' }
  ];

  const handleNavClick = (viewId) => {
    setActiveView(viewId);
    setIsMobileOpen(false);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div className="sidenav-overlay" onClick={() => setIsMobileOpen(false)} />
      )}

      <aside className={`sidenav ${isMobileOpen ? 'open' : ''}`}>
        <div className="sidenav-header">
          <div className="sidenav-logo">
            <span className="sidenav-logo-icon">💰</span>
            <span className="sidenav-logo-text">CreatorCents</span>
          </div>
          <button className="sidenav-close" onClick={() => setIsMobileOpen(false)}>×</button>
        </div>

        <nav className="sidenav-nav">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`sidenav-item ${activeView === item.id ? 'active' : ''}`}
              onClick={() => handleNavClick(item.id)}
            >
              <span className="sidenav-item-icon">{item.icon}</span>
              <span className="sidenav-item-label">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidenav-footer">
          {isDemo ? (
            <>
              <div className="sidenav-user">
                <span className="sidenav-email">Demo Mode</span>
                <span className="sidenav-plan-badge demo">Demo</span>
              </div>
              <button type="button" onClick={onExitDemo} className="sidenav-signout">Sign Up to Save</button>
            </>
          ) : (
            <>
              <div className="sidenav-user">
                <span className="sidenav-email" title={user?.email}>{user?.email}</span>
                <span className={`sidenav-plan-badge ${isPro ? 'pro' : 'basic'}`}>
                  {isPro ? 'Pro' : 'Basic'}
                </span>
              </div>
              <button type="button" onClick={onSignOut} className="sidenav-signout">Sign Out</button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

// Mobile Header Component
function MobileHeader({ onMenuClick, isDemo, onExitDemo }) {
  return (
    <header className="mobile-header">
      <button className="hamburger-button" onClick={onMenuClick}>
        <span></span>
        <span></span>
        <span></span>
      </button>
      <div className="mobile-header-logo">CreatorCents</div>
      {isDemo && (
        <button onClick={onExitDemo} className="mobile-header-cta">Sign Up</button>
      )}
    </header>
  );
}

// Reports View Component - Copilot-style Dashboard
function ReportsView({ user, isDemo, entries: dashboardEntries }) {
  const [timePeriod, setTimePeriod] = useState('3M');
  const [allEntries, setAllEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Time period options
  const TIME_PERIODS = [
    { id: '1W', label: '1W', days: 7 },
    { id: '1M', label: '1M', days: 30 },
    { id: '3M', label: '3M', days: 90 },
    { id: 'YTD', label: 'YTD', days: null },
    { id: '1Y', label: '1Y', days: 365 },
    { id: 'ALL', label: 'ALL', days: null }
  ];

  // Fetch ALL entries (not filtered by year) for Reports
  useEffect(() => {
    const fetchAllEntries = async () => {
      if (isDemo) {
        console.log('[ReportsView] Demo mode - using dashboard entries');
        setAllEntries(dashboardEntries);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('[ReportsView] Fetching all entries');

        // Fetch all entries (no date filter for ALL time support)
        const { data, error } = await supabase
          .from('expenses')
          .select('*')
          .eq('user_id', user.id)
          .order('expense_date', { ascending: false });

        if (error) {
          console.error('[ReportsView] Error fetching entries:', error);
          throw error;
        }

        console.log('[ReportsView] Raw data from DB:', data?.length, 'entries');

        const mapped = (data || []).map(row => ({
          id: row.id,
          category: normalizeCategory(row.category),
          subcategory: row.subcategory || null,
          amount: parseFloat(row.amount),
          description: row.description,
          date: row.expense_date,
          receiptPath: row.receipt_path,
          receiptName: row.receipt_name
        }));

        console.log('[ReportsView] Mapped entries:', mapped.length);
        console.log('[ReportsView] Sample entries:', mapped.slice(0, 5));

        // Debug: Check categories distribution
        const categoryCount = {};
        mapped.forEach(e => {
          categoryCount[e.category] = (categoryCount[e.category] || 0) + 1;
        });
        console.log('[ReportsView] Category distribution:', categoryCount);

        setAllEntries(mapped);
      } catch (err) {
        console.error('[ReportsView] Error:', err);
        setAllEntries(dashboardEntries || []);
      } finally {
        setLoading(false);
      }
    };

    fetchAllEntries();
  }, [user?.id, isDemo]);

  // Debug: Log when time period or data changes
  useEffect(() => {
    console.log('[ReportsView] ===== TIME PERIOD CHANGE =====');
    console.log('[ReportsView] Selected period:', timePeriod);
    console.log('[ReportsView] All entries available:', allEntries.length);

    // Log income entries specifically
    const incomeEntries = allEntries.filter(e => e.category === 'income');
    console.log('[ReportsView] Income entries in allEntries:', incomeEntries.length);
    if (incomeEntries.length > 0) {
      console.log('[ReportsView] Sample income entries:', incomeEntries.slice(0, 3));
    }
  }, [timePeriod, allEntries]);

  // Get date range for selected period
  const getDateRange = (period) => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    let startDate;

    switch (period) {
      case '1W':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '1M':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '3M':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '1Y':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      case 'ALL':
        startDate = new Date('2000-01-01'); // Far back enough for all data
        break;
      case 'YTD':
      default:
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
    }

    const range = {
      start: startDate.toISOString().split('T')[0],
      end: today
    };

    console.log(`[ReportsView] Current period (${period}):`, range);
    return range;
  };

  // Get previous period date range for comparison
  const getPreviousPeriodRange = (period) => {
    const now = new Date();
    let startDate, endDate;

    switch (period) {
      case '1W':
        endDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000 - 1);
        startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '1M':
        endDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000 - 1);
        startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '3M':
        endDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000 - 1);
        startDate = new Date(endDate.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '1Y':
        endDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000 - 1);
        startDate = new Date(endDate.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      case 'ALL':
        // No meaningful comparison for ALL time
        startDate = new Date('1999-01-01');
        endDate = new Date('1999-12-31');
        break;
      case 'YTD':
      default:
        // Same period last year (Jan 1 to same day last year)
        const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 1)) / (24 * 60 * 60 * 1000));
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear() - 1, 0, 1 + dayOfYear);
        break;
    }

    const range = {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };

    console.log(`[ReportsView] Previous period (${period}):`, range);
    return range;
  };

  const currentRange = getDateRange(timePeriod);
  const previousRange = getPreviousPeriodRange(timePeriod);

  // Filter entries by date range
  const filterByDateRange = (entriesList, range, label) => {
    const filtered = entriesList.filter(e => e.date >= range.start && e.date <= range.end);
    console.log(`[ReportsView] ${label}: ${filtered.length} entries in range ${range.start} to ${range.end}`);
    return filtered;
  };

  const currentEntries = filterByDateRange(allEntries, currentRange, 'Current period');
  const previousEntries = filterByDateRange(allEntries, previousRange, 'Previous period');

  // Calculate totals
  const calculateTotals = (entriesList, label) => {
    const incomeEntries = entriesList.filter(e => {
      const cat = CATEGORIES.find(c => c.id === e.category);
      return cat?.isIncome;
    });

    const expenseEntries = entriesList.filter(e => {
      const cat = CATEGORIES.find(c => c.id === e.category);
      return cat && !cat.isIncome && cat.affectsTotals;
    });

    const income = incomeEntries.reduce((sum, e) => sum + e.amount, 0);
    const expenses = expenseEntries.reduce((sum, e) => sum + e.amount, 0);

    console.log(`[ReportsView] ${label} totals:`, {
      incomeEntries: incomeEntries.length,
      income,
      expenseEntries: expenseEntries.length,
      expenses
    });

    return { income, expenses };
  };

  const currentTotals = calculateTotals(currentEntries, 'Current');
  const previousTotals = calculateTotals(previousEntries, 'Previous');

  // Calculate percentage change
  const calculateChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const expenseChange = calculateChange(currentTotals.expenses, previousTotals.expenses);
  const incomeChange = calculateChange(currentTotals.income, previousTotals.income);

  console.log('[ReportsView] Changes:', { expenseChange, incomeChange });

  // Category breakdown for current period
  const expenseCategories = CATEGORIES.filter(cat => !cat.isIncome && cat.affectsTotals);
  const categoryTotals = expenseCategories.map(cat => {
    const total = currentEntries
      .filter(e => e.category === cat.id)
      .reduce((sum, e) => sum + e.amount, 0);
    return {
      id: cat.id,
      name: cat.label,
      icon: cat.icon,
      value: total,
      color: cat.color
    };
  }).filter(item => item.value > 0)
    .sort((a, b) => b.value - a.value);

  const maxCategoryValue = categoryTotals.length > 0
    ? Math.max(...categoryTotals.map(c => c.value))
    : 0;

  // Monthly stacked data for chart
  const getStackedMonthlyData = () => {
    const data = [];
    const now = new Date();

    // Determine display mode and count based on time period
    let displayMode = 'months'; // 'days', 'weeks', or 'months'
    let count = 12;

    switch (timePeriod) {
      case '1W':
        displayMode = 'days';
        count = 7;
        break;
      case '1M':
        displayMode = 'weeks';
        count = 4;
        break;
      case '3M':
        displayMode = 'months';
        count = 3;
        break;
      case 'YTD':
        displayMode = 'months';
        count = now.getMonth() + 1; // Months from Jan to current
        break;
      case '1Y':
        displayMode = 'months';
        count = 12;
        break;
      case 'ALL':
        displayMode = 'months';
        count = 24; // Show last 24 months for ALL
        break;
      default:
        displayMode = 'months';
        count = 12;
    }

    if (displayMode === 'days') {
      // Show daily data for 1W
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dayLabel = date.toLocaleDateString('en-US', { weekday: 'short' });
        const dateStr = date.toISOString().split('T')[0];

        const dayData = { name: dayLabel };
        expenseCategories.forEach(cat => {
          const total = allEntries
            .filter(e => e.date === dateStr && e.category === cat.id)
            .reduce((sum, e) => sum + e.amount, 0);
          dayData[cat.id] = total;
        });
        data.push(dayData);
      }
    } else if (displayMode === 'weeks') {
      // Show weekly data for 1M
      for (let i = 3; i >= 0; i--) {
        const weekEnd = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
        const weekStart = new Date(weekEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
        const weekLabel = `W${4 - i}`;

        const weekData = { name: weekLabel };
        expenseCategories.forEach(cat => {
          const total = allEntries
            .filter(e => {
              const entryDate = new Date(e.date);
              return e.category === cat.id && entryDate >= weekStart && entryDate <= weekEnd;
            })
            .reduce((sum, e) => sum + e.amount, 0);
          weekData[cat.id] = total;
        });
        data.push(weekData);
      }
    } else {
      // Show monthly data
      for (let i = count - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStr = date.toLocaleDateString('en-US', { month: 'short' });
        const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        const monthData = { name: monthStr };
        expenseCategories.forEach(cat => {
          const total = allEntries
            .filter(e => e.date.startsWith(yearMonth) && e.category === cat.id)
            .reduce((sum, e) => sum + e.amount, 0);
          monthData[cat.id] = total;
        });
        data.push(monthData);
      }
    }
    return data;
  };

  const stackedMonthlyData = getStackedMonthlyData();

  // Income vs Expenses data
  const getIncomeExpenseData = () => {
    const data = [];
    const now = new Date();

    // Determine display mode and count based on time period
    let displayMode = 'months';
    let count = 12;

    switch (timePeriod) {
      case '1W':
        displayMode = 'days';
        count = 7;
        break;
      case '1M':
        displayMode = 'weeks';
        count = 4;
        break;
      case '3M':
        displayMode = 'months';
        count = 3;
        break;
      case 'YTD':
        displayMode = 'months';
        count = now.getMonth() + 1;
        break;
      case '1Y':
        displayMode = 'months';
        count = 12;
        break;
      case 'ALL':
        displayMode = 'months';
        count = 24;
        break;
      default:
        displayMode = 'months';
        count = 12;
    }

    const calculateIncomeExpense = (entries) => {
      const income = entries
        .filter(e => CATEGORIES.find(c => c.id === e.category)?.isIncome)
        .reduce((sum, e) => sum + e.amount, 0);
      const expenses = entries
        .filter(e => {
          const cat = CATEGORIES.find(c => c.id === e.category);
          return cat && !cat.isIncome && cat.affectsTotals;
        })
        .reduce((sum, e) => sum + e.amount, 0);
      return { income, expenses };
    };

    if (displayMode === 'days') {
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dayLabel = date.toLocaleDateString('en-US', { weekday: 'short' });
        const dateStr = date.toISOString().split('T')[0];
        const dayEntries = allEntries.filter(e => e.date === dateStr);
        const { income, expenses } = calculateIncomeExpense(dayEntries);
        data.push({ name: dayLabel, income, expenses });
      }
    } else if (displayMode === 'weeks') {
      for (let i = 3; i >= 0; i--) {
        const weekEnd = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
        const weekStart = new Date(weekEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
        const weekLabel = `W${4 - i}`;
        const weekEntries = allEntries.filter(e => {
          const entryDate = new Date(e.date);
          return entryDate >= weekStart && entryDate <= weekEnd;
        });
        const { income, expenses } = calculateIncomeExpense(weekEntries);
        data.push({ name: weekLabel, income, expenses });
      }
    } else {
      for (let i = count - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStr = date.toLocaleDateString('en-US', { month: 'short' });
        const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthEntries = allEntries.filter(e => e.date.startsWith(yearMonth));
        const { income, expenses } = calculateIncomeExpense(monthEntries);
        data.push({ name: monthStr, income, expenses });
      }
    }
    return data;
  };

  const incomeExpenseData = getIncomeExpenseData();

  // Format helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}k`;
    }
    return `$${amount.toFixed(0)}`;
  };

  const getPeriodLabel = () => {
    switch (timePeriod) {
      case '1W': return 'last 7 days';
      case '1M': return 'last 30 days';
      case '3M': return 'last 90 days';
      case 'YTD': return 'year to date';
      case '1Y': return 'last 365 days';
      case 'ALL': return 'all time';
      default: return '';
    }
  };

  const getPreviousPeriodLabel = () => {
    switch (timePeriod) {
      case '1W': return 'previous 7 days';
      case '1M': return 'previous 30 days';
      case '3M': return 'previous 90 days';
      case 'YTD': return 'same period last year';
      case '1Y': return 'previous 365 days';
      case 'ALL': return 'N/A';
      default: return 'previous period';
    }
  };

  if (loading) {
    return (
      <div className="reports-view reports-copilot">
        <h1 className="view-title">Cash Flow</h1>
        <p className="view-subtitle">Loading data...</p>
      </div>
    );
  }

  return (
    <div className="reports-view reports-copilot">
      <div className="reports-header">
        <div>
          <h1 className="view-title">Cash Flow</h1>
          <p className="view-subtitle">Financial overview for {getPeriodLabel()}</p>
        </div>

        {/* Time Period Selector */}
        <div className="time-period-selector">
          {TIME_PERIODS.map(period => (
            <button
              key={period.id}
              className={`period-button ${timePeriod === period.id ? 'active' : ''}`}
              onClick={() => setTimePeriod(period.id)}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards-row">
        {/* Total Spending Card */}
        <div className="summary-card-large">
          <div className="summary-card-label">Total Spending</div>
          <div className="summary-card-value expenses">{formatCurrency(currentTotals.expenses)}</div>
          <div className="summary-card-comparison">
            <span className={`change-indicator ${expenseChange <= 0 ? 'positive' : 'negative'}`}>
              {expenseChange <= 0 ? '↓' : '↑'} {Math.abs(expenseChange).toFixed(1)}%
            </span>
            <span className="comparison-text">
              vs {formatCurrency(previousTotals.expenses)} {getPreviousPeriodLabel()}
            </span>
          </div>
        </div>

        {/* Total Income Card */}
        <div className="summary-card-large">
          <div className="summary-card-label">Total Income</div>
          <div className="summary-card-value income">{formatCurrency(currentTotals.income)}</div>
          <div className="summary-card-comparison">
            <span className={`change-indicator ${incomeChange >= 0 ? 'positive' : 'negative'}`}>
              {incomeChange >= 0 ? '↑' : '↓'} {Math.abs(incomeChange).toFixed(1)}%
            </span>
            <span className="comparison-text">
              vs {formatCurrency(previousTotals.income)} {getPreviousPeriodLabel()}
            </span>
          </div>
        </div>

        {/* Net Profit Card */}
        <div className="summary-card-large">
          <div className="summary-card-label">Net Profit</div>
          <div className={`summary-card-value ${currentTotals.income - currentTotals.expenses >= 0 ? 'income' : 'expenses'}`}>
            {formatCurrency(currentTotals.income - currentTotals.expenses)}
          </div>
          <div className="summary-card-comparison">
            <span className="comparison-text">
              {currentEntries.length} transactions
            </span>
          </div>
        </div>
      </div>

      {/* Stacked Bar Chart */}
      <div className="chart-card-full">
        <h3 className="chart-title">Spending by Category</h3>
        {stackedMonthlyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stackedMonthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="name" stroke="#6b7280" axisLine={false} tickLine={false} />
              <YAxis stroke="#6b7280" axisLine={false} tickLine={false} tickFormatter={formatCompactCurrency} />
              <Tooltip
                formatter={(value, name) => [formatCurrency(value), CATEGORIES.find(c => c.id === name)?.label || name]}
                contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#111827', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                labelStyle={{ color: '#111827' }}
                itemStyle={{ color: '#374151' }}
              />
              {expenseCategories.map((cat) => (
                <Bar
                  key={cat.id}
                  dataKey={cat.id}
                  stackId="a"
                  fill={cat.color}
                  radius={[0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="chart-empty">No data for this period</div>
        )}
      </div>

      {/* Category Breakdown with Progress Bars */}
      <div className="chart-card-full">
        <h3 className="chart-title">Expense Breakdown</h3>
        {categoryTotals.length > 0 ? (
          <div className="category-breakdown-fixed">
            {categoryTotals.map((cat, index) => (
              <div
                key={cat.id}
                className="category-row-fixed"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Fixed-width label */}
                <div className="category-label-fixed">
                  <span className="category-icon">{cat.icon}</span>
                  <span className="category-name">{cat.name}</span>
                </div>
                {/* Progress bar */}
                <div className="category-bar-container">
                  <div
                    className="category-bar-fill"
                    style={{
                      width: `${Math.max((cat.value / maxCategoryValue) * 100, 8)}%`,
                      backgroundColor: cat.color
                    }}
                  />
                </div>
                {/* Fixed-width amount */}
                <div className="category-amount-fixed">{formatCurrency(cat.value)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="chart-empty">No expenses for this period</div>
        )}
      </div>

      {/* Income vs Expenses Chart */}
      <div className="chart-card-full">
        <h3 className="chart-title">Income vs Expenses</h3>
        {incomeExpenseData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={incomeExpenseData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="name" stroke="#6b7280" axisLine={false} tickLine={false} />
              <YAxis stroke="#6b7280" axisLine={false} tickLine={false} tickFormatter={formatCompactCurrency} />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#111827', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                labelStyle={{ color: '#111827' }}
                itemStyle={{ color: '#374151' }}
              />
              <Legend wrapperStyle={{ color: '#6b7280' }} />
              <Bar dataKey="income" name="Income" fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#dc2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="chart-empty">No data for this period</div>
        )}
      </div>
    </div>
  );
}

// Receipts Gallery View Component
function ReceiptsView({ entries, user, isDemo, filterYear }) {
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [receiptUrls, setReceiptUrls] = useState({});
  const [allReceipts, setAllReceipts] = useState([]);
  const [loadingAllReceipts, setLoadingAllReceipts] = useState(false);
  const [failedImages, setFailedImages] = useState({});

  // Date range filter - default to current month for better performance
  const getDefaultFromDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  const getDefaultToDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [fromDate, setFromDate] = useState(getDefaultFromDate);
  const [toDate, setToDate] = useState(getDefaultToDate);
  const [dateFilterActive, setDateFilterActive] = useState(true);

  // Pagination
  const RECEIPTS_PER_PAGE = 20;
  const [visibleCount, setVisibleCount] = useState(RECEIPTS_PER_PAGE);

  // Track loaded images for fade-in effect
  const [loadedImages, setLoadedImages] = useState({});

  // Helper: Check if a string is a full URL
  const isFullUrl = (path) => {
    return path && (path.startsWith('http://') || path.startsWith('https://'));
  };

  // Helper: Check if file is a PDF
  const isPdfFile = (path) => {
    if (!path) return false;
    const lowerPath = path.toLowerCase();
    return lowerPath.endsWith('.pdf') || lowerPath.includes('.pdf?');
  };

  // Helper: Check path format
  // Old format: "userId/timestamp-filename.png" (has folder prefix)
  // New format: "timestamp-filename.png" (no folder prefix)
  const hasUserIdPrefix = (path) => {
    return path && path.includes('/');
  };

  // Helper: Construct public URL for Supabase storage
  const getPublicUrl = (path) => {
    if (!path) return null;
    // Get Supabase URL from environment or construct from supabase client
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
    if (!supabaseUrl) {
      console.warn('[ReceiptsView] No Supabase URL configured');
      return null;
    }
    return `${supabaseUrl}/storage/v1/object/public/receipts/${path}`;
  };

  // Helper: Get the display URL for a receipt
  const getReceiptUrl = (entry) => {
    // First check if we have a URL cached
    if (receiptUrls[entry.id]) {
      return receiptUrls[entry.id];
    }
    // If the stored path is already a full URL, use it directly
    if (isFullUrl(entry.receiptPath)) {
      return entry.receiptPath;
    }
    return null;
  };

  // Fetch ALL entries with receipts (not filtered by year)
  useEffect(() => {
    const fetchAllReceipts = async () => {
      if (isDemo) {
        // In demo mode, just use the passed entries
        const withReceipts = entries.filter(e => e.receiptPath);
        console.log('[ReceiptsView] Demo mode - entries with receipts:', withReceipts);
        setAllReceipts(withReceipts);
        return;
      }

      setLoadingAllReceipts(true);
      try {
        // Fetch ALL entries that have a receipt_path (no year filter)
        const { data, error } = await supabase
          .from('expenses')
          .select('*')
          .eq('user_id', user.id)
          .not('receipt_path', 'is', null)
          .order('expense_date', { ascending: false });

        if (error) {
          console.error('[ReceiptsView] Error fetching receipts:', error);
          throw error;
        }

        console.log('[ReceiptsView] Raw data from database:', data);
        console.log('[ReceiptsView] Number of entries with receipt_path in DB:', data?.length || 0);

        // Debug: Log each receipt_path to see what format they're in
        data?.forEach((row, idx) => {
          console.log(`[ReceiptsView] Receipt ${idx + 1}:`, {
            id: row.id,
            receipt_path: row.receipt_path,
            isFullUrl: row.receipt_path?.startsWith('http'),
            date: row.expense_date
          });
        });

        const mapped = (data || []).map(row => ({
          id: row.id,
          category: normalizeCategory(row.category),
          subcategory: row.subcategory || null,
          amount: parseFloat(row.amount),
          description: row.description,
          date: row.expense_date,
          receiptPath: row.receipt_path,
          receiptName: row.receipt_name
        }));

        console.log('[ReceiptsView] Mapped entries:', mapped);
        setAllReceipts(mapped);
      } catch (err) {
        console.error('[ReceiptsView] Error:', err);
        // Fallback to filtered entries
        const withReceipts = entries.filter(e => e.receiptPath);
        setAllReceipts(withReceipts);
      } finally {
        setLoadingAllReceipts(false);
      }
    };

    fetchAllReceipts();
  }, [user?.id, isDemo]);

  // Debug: Log what we're working with
  useEffect(() => {
    console.log('[ReceiptsView] Current filterYear:', filterYear);
    console.log('[ReceiptsView] Entries passed from parent (year-filtered):', entries.length);
    console.log('[ReceiptsView] Entries with receiptPath from parent:', entries.filter(e => e.receiptPath).length);
    console.log('[ReceiptsView] All receipts fetched (no year filter):', allReceipts.length);
  }, [entries, allReceipts, filterYear]);

  // Use allReceipts instead of filtering from year-filtered entries
  const entriesWithReceipts = allReceipts;

  // Apply filters: category and date range
  const filteredReceipts = entriesWithReceipts.filter(entry => {
    // Category filter
    if (filterCategory !== 'all' && entry.category !== filterCategory) {
      return false;
    }

    // Date range filter (only if dates are set)
    if (dateFilterActive && (fromDate || toDate)) {
      // Normalize entry date to YYYY-MM-DD format (handles timestamps and various formats)
      const entryDateStr = entry.date ? entry.date.substring(0, 10) : '';

      if (!entryDateStr) {
        return false; // Skip entries without valid dates
      }

      // Compare using normalized date strings (YYYY-MM-DD format ensures correct lexicographic comparison)
      if (fromDate && entryDateStr < fromDate) {
        return false;
      }
      if (toDate && entryDateStr > toDate) {
        return false;
      }
    }

    return true;
  });

  // Paginated receipts
  const paginatedReceipts = filteredReceipts.slice(0, visibleCount);
  const hasMoreReceipts = filteredReceipts.length > visibleCount;

  // Load more receipts
  const loadMoreReceipts = () => {
    setVisibleCount(prev => prev + RECEIPTS_PER_PAGE);
  };

  // Reset pagination when filters change
  useEffect(() => {
    setVisibleCount(RECEIPTS_PER_PAGE);
  }, [filterCategory, fromDate, toDate, dateFilterActive]);

  // Clear all filters (reset dates to current month defaults)
  const clearFilters = () => {
    setFilterCategory('all');
    setFromDate(getDefaultFromDate());
    setToDate(getDefaultToDate());
    setDateFilterActive(true);
    setVisibleCount(RECEIPTS_PER_PAGE);
  };

  // Check if any filters are active (beyond defaults)
  const hasActiveFilters = filterCategory !== 'all' ||
    (fromDate !== getDefaultFromDate()) ||
    (toDate !== getDefaultToDate());

  // Handle image load success for fade-in
  const handleImageLoad = (entryId) => {
    setLoadedImages(prev => ({ ...prev, [entryId]: true }));
  };

  // Open PDF in new tab
  const openPdfInNewTab = (url, e) => {
    e.stopPropagation();
    window.open(url, '_blank');
  };

  // Get URLs for VISIBLE receipts only - load in parallel for speed
  useEffect(() => {
    const loadReceiptUrls = async () => {
      if (isDemo || paginatedReceipts.length === 0) return;

      // Only load URLs for receipts that don't have URLs yet
      const receiptsNeedingUrls = paginatedReceipts.filter(
        entry => entry.receiptPath && !receiptUrls[entry.id]
      );

      if (receiptsNeedingUrls.length === 0) return;

      console.log('[ReceiptsView] Loading URLs for', receiptsNeedingUrls.length, 'visible receipts');

      // Load URLs in parallel for speed
      const urlPromises = receiptsNeedingUrls.map(async (entry) => {
        const path = entry.receiptPath;

        // Check if it's already a full URL
        if (isFullUrl(path)) {
          return { id: entry.id, url: path };
        }

        // Try to get signed URL first (works for both formats)
        try {
          const { data, error } = await supabase.storage
            .from('receipts')
            .createSignedUrl(path, 3600);

          if (data?.signedUrl) {
            return { id: entry.id, url: data.signedUrl };
          }
        } catch (err) {
          // Signed URL failed, try fallback
        }

        // Fallback: Try public URL
        const publicUrl = getPublicUrl(path);
        if (publicUrl) {
          return { id: entry.id, url: publicUrl };
        }

        // Last resort: Try getPublicUrl from Supabase client
        try {
          const { data } = supabase.storage
            .from('receipts')
            .getPublicUrl(path);

          if (data?.publicUrl) {
            return { id: entry.id, url: data.publicUrl };
          }
        } catch (err) {
          console.error('[ReceiptsView] All URL methods failed for entry', entry.id);
        }

        return { id: entry.id, url: null };
      });

      // Wait for all URLs to load in parallel
      const results = await Promise.all(urlPromises);

      // Build the URLs object from results
      const urls = {};
      results.forEach(result => {
        if (result.url) {
          urls[result.id] = result.url;
        }
      });

      console.log('[ReceiptsView] Loaded', Object.keys(urls).length, 'URLs');
      if (Object.keys(urls).length > 0) {
        setReceiptUrls(prev => ({ ...prev, ...urls }));
      }
    };

    loadReceiptUrls();
  }, [paginatedReceipts, isDemo]);

  // Handle image load error
  const handleImageError = (entryId, url) => {
    console.error('[ReceiptsView] Image failed to load for entry', entryId, '- URL:', url);
    setFailedImages(prev => ({ ...prev, [entryId]: true }));
  };

  const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId) || { label: 'Unknown', icon: '❓' };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (loadingAllReceipts) {
    return (
      <div className="receipts-view">
        <h1 className="view-title">Receipts</h1>
        <div className="receipts-loading">
          <div className="receipts-loading-spinner"></div>
          <p>Loading your receipts...</p>
          <span className="receipts-loading-hint">This may take a moment</span>
        </div>
      </div>
    );
  }

  return (
    <div className="receipts-view">
      <h1 className="view-title">Receipts</h1>
      <p className="view-subtitle">
        {filteredReceipts.length} of {entriesWithReceipts.length} receipts
        {hasActiveFilters && ' (filtered)'}
      </p>

      <div className="receipts-filters">
        <div className="receipts-filter-row">
          <div className="filter-group">
            <label>Category:</label>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="all">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group date-filter-group">
            <label>From:</label>
            <div className="date-input-wrapper">
              <input
                type="date"
                value={fromDate}
                className={fromDate ? 'has-value' : ''}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setDateFilterActive(true);
                }}
              />
              {!fromDate && <span className="date-placeholder">Start date</span>}
            </div>
          </div>

          <div className="filter-group date-filter-group">
            <label>To:</label>
            <div className="date-input-wrapper">
              <input
                type="date"
                value={toDate}
                className={toDate ? 'has-value' : ''}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setDateFilterActive(true);
                }}
              />
              {!toDate && <span className="date-placeholder">End date</span>}
            </div>
          </div>

          {hasActiveFilters && (
            <button onClick={clearFilters} className="clear-filter-button">
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {filteredReceipts.length === 0 ? (
        <div className="receipts-empty">
          <div className="receipts-empty-icon">🧾</div>
          <p>No receipts found</p>
          <p className="receipts-empty-hint">Upload receipts when adding entries to see them here</p>
        </div>
      ) : (
        <>
          <div className="receipts-grid">
            {paginatedReceipts.map(entry => {
              const cat = getCategoryInfo(entry.category);
              const url = getReceiptUrl(entry);
              const hasFailed = failedImages[entry.id];
              const isLoaded = loadedImages[entry.id];
              const isPdf = isPdfFile(entry.receiptPath);

              return (
                <div
                  key={entry.id}
                  className="receipt-card"
                  onClick={() => {
                    if (isPdf && url) {
                      window.open(url, '_blank');
                    } else if (url && !hasFailed) {
                      setSelectedReceipt({ ...entry, url });
                    }
                  }}
                >
                  <div className="receipt-thumbnail">
                    {isPdf && url ? (
                      <div className="receipt-pdf-placeholder">
                        <span className="pdf-icon">📄</span>
                        <small>PDF Receipt</small>
                        <button
                          className="view-pdf-button"
                          onClick={(e) => openPdfInNewTab(url, e)}
                        >
                          View PDF
                        </button>
                      </div>
                    ) : url && !hasFailed ? (
                      <>
                        {!isLoaded && (
                          <div className="receipt-skeleton">
                            <div className="skeleton-shimmer"></div>
                          </div>
                        )}
                        <img
                          src={url}
                          alt="Receipt"
                          loading="lazy"
                          className={`receipt-image ${isLoaded ? 'loaded' : 'loading'}`}
                          onLoad={() => handleImageLoad(entry.id)}
                          onError={() => handleImageError(entry.id, url)}
                        />
                      </>
                    ) : hasFailed ? (
                      <div className="receipt-placeholder receipt-error">
                        <span>⚠️</span>
                        <small>Failed to load</small>
                      </div>
                    ) : (
                      <div className="receipt-skeleton">
                        <div className="skeleton-shimmer"></div>
                      </div>
                    )}
                  </div>
                  <div className="receipt-info">
                    <div className="receipt-date">{entry.date}</div>
                    <div className="receipt-amount">{entry.category === 'miles' ? `${entry.amount.toFixed(1)} mi` : formatCurrency(entry.amount)}</div>
                    <div className="receipt-category">
                      <span>{cat.icon}</span> {cat.label}
                    </div>
                    {isPdf && <div className="receipt-type-badge">PDF</div>}
                    {hasFailed && <div className="receipt-error-text">Image unavailable</div>}
                  </div>
                </div>
              );
            })}
          </div>

          {hasMoreReceipts && (
            <div className="load-more-container">
              <button onClick={loadMoreReceipts} className="load-more-button">
                Load More ({filteredReceipts.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </>
      )}

      {/* Receipt Modal */}
      {selectedReceipt && (
        <div className="receipt-modal-overlay" onClick={() => setSelectedReceipt(null)}>
          <div className="receipt-modal" onClick={e => e.stopPropagation()}>
            <button className="receipt-modal-close" onClick={() => setSelectedReceipt(null)}>×</button>
            {failedImages[selectedReceipt.id] ? (
              <div className="receipt-modal-error">
                <span>⚠️</span>
                <p>Failed to load receipt image</p>
                <small>Path: {selectedReceipt.receiptPath}</small>
              </div>
            ) : (
              <img
                src={selectedReceipt.url}
                alt="Receipt"
                className="receipt-modal-image"
                onError={() => handleImageError(selectedReceipt.id, selectedReceipt.url)}
              />
            )}
            <div className="receipt-modal-info">
              <div><strong>Date:</strong> {selectedReceipt.date}</div>
              <div><strong>Amount:</strong> {selectedReceipt.category === 'miles' ? `${selectedReceipt.amount.toFixed(1)} mi` : formatCurrency(selectedReceipt.amount)}</div>
              <div><strong>Description:</strong> {selectedReceipt.description}</div>
              <div><strong>Category:</strong> {getCategoryInfo(selectedReceipt.category).label}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Settings View Component
function SettingsView({ user, subscription, isDemo, onExitDemo }) {
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [homeAddress, setHomeAddress] = useState(null);
  const [editingHome, setEditingHome] = useState(false);
  const [homeAddressInput, setHomeAddressInput] = useState('');
  const [savingHome, setSavingHome] = useState(false);

  const isPro = subscription?.plan === 'pro_monthly' || subscription?.plan === 'pro_yearly';
  const planName = isPro ? 'Pro' : 'Basic';
  const billingCycle = subscription?.plan?.includes('yearly') ? 'Yearly' : 'Monthly';

  // Fetch home address on mount
  useEffect(() => {
    if (isDemo || !user) return;

    const fetchHomeAddress = async () => {
      const { data, error } = await supabase
        .from('saved_locations')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_home', true)
        .maybeSingle(); // Use maybeSingle to avoid error when no home address exists

      if (!error && data) {
        setHomeAddress(data);
        setHomeAddressInput(data.address);
      }
    };

    fetchHomeAddress();
  }, [user, isDemo]);

  const handleSaveHomeAddress = async () => {
    if (!homeAddressInput.trim()) return;

    setSavingHome(true);
    try {
      if (homeAddress) {
        // Update existing
        const { error } = await supabase
          .from('saved_locations')
          .update({ address: homeAddressInput.trim() })
          .eq('id', homeAddress.id);

        if (error) throw error;
        setHomeAddress({ ...homeAddress, address: homeAddressInput.trim() });
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('saved_locations')
          .insert({
            user_id: user.id,
            name: 'Home',
            address: homeAddressInput.trim(),
            is_home: true
          })
          .select()
          .single();

        if (error) throw error;
        setHomeAddress(data);
      }
      setEditingHome(false);
    } catch (err) {
      console.error('Error saving home address:', err);
      alert('Failed to save address. Please try again.');
    } finally {
      setSavingHome(false);
    }
  };

  const handleManageSubscription = async () => {
    if (isDemo) return;

    try {
      const response = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id })
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Error opening subscription portal:', err);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      alert('Please type DELETE to confirm');
      return;
    }

    setDeleting(true);
    try {
      console.log('[Settings] Starting account deletion for user:', user.id);

      // Call the delete-account API (uses service role key for admin operations)
      const response = await fetch('/api/delete-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id })
      });

      const data = await response.json();
      console.log('[Settings] Delete account API response:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete account');
      }

      // Sign out locally
      console.log('[Settings] Signing out...');
      await supabase.auth.signOut();

      // Show success message and redirect
      alert('Your account has been deleted successfully.');

      // Force redirect to landing page
      window.location.href = '/';
    } catch (err) {
      console.error('[Settings] Error deleting account:', err);
      alert('Failed to delete account: ' + err.message + '\n\nPlease contact support@creatorcents.app');
    } finally {
      setDeleting(false);
    }
  };

  if (isDemo) {
    return (
      <div className="settings-view">
        <h1 className="view-title">Settings</h1>
        <div className="settings-section">
          <h2>Demo Mode</h2>
          <p>You're currently in demo mode. Sign up to save your data and access all features.</p>
          <button onClick={onExitDemo} className="settings-button primary">Sign Up Now</button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-view">
      <h1 className="view-title">Settings</h1>

      <div className="settings-section">
        <h2>Account</h2>
        <div className="settings-row">
          <span className="settings-label">Email</span>
          <span className="settings-value">{user?.email}</span>
        </div>
        <div className="settings-row">
          <span className="settings-label">Plan</span>
          <span className="settings-value">
            <span className={`settings-plan-badge ${isPro ? 'pro' : 'basic'}`}>{planName}</span>
            <span className="settings-billing-cycle">{billingCycle}</span>
          </span>
        </div>
      </div>

      <div className="settings-section">
        <h2>Subscription</h2>
        <p className="settings-description">
          Manage your subscription, change plans, update payment method, or view invoices.
        </p>
        <button onClick={handleManageSubscription} className="settings-button primary">
          Manage Subscription
        </button>
      </div>

      <div className="settings-section">
        <h2>Mileage Tracking</h2>
        <p className="settings-description">
          Set your home address for quick trip logging.
          {!isPro && ' Pro plan includes automatic distance calculation.'}
        </p>
        <div className="settings-row">
          <span className="settings-label">Home Address</span>
          <span className="settings-value">
            {editingHome ? (
              <div className="home-address-edit">
                <AddressAutocomplete
                  value={homeAddressInput}
                  onChange={setHomeAddressInput}
                  placeholder="Enter your home address"
                  disabled={false}
                  className="home-address-input"
                />
                <div className="home-address-actions">
                  <button
                    onClick={handleSaveHomeAddress}
                    disabled={savingHome || !homeAddressInput.trim()}
                    className="settings-button small primary"
                  >
                    {savingHome ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingHome(false);
                      setHomeAddressInput(homeAddress?.address || '');
                    }}
                    className="settings-button small"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : homeAddress ? (
              <div className="home-address-display">
                <span className="home-address-text">{homeAddress.address}</span>
                <button
                  onClick={() => setEditingHome(true)}
                  className="settings-edit-link"
                >
                  Edit
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditingHome(true)}
                className="settings-button small"
              >
                + Add Home Address
              </button>
            )}
          </span>
        </div>
      </div>

      <div className="settings-section danger">
        <h2>Danger Zone</h2>
        <div className="delete-warning">
          <p className="delete-warning-title">This will permanently delete ALL your data including:</p>
          <ul className="delete-warning-list">
            <li>All expense entries</li>
            <li>All uploaded receipts</li>
            <li>Your subscription (canceled immediately)</li>
          </ul>
          <p className="delete-warning-final">This cannot be undone. Type DELETE to confirm.</p>
        </div>
        <div className="delete-confirm">
          <input
            type="text"
            placeholder="Type DELETE to confirm"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            className="delete-confirm-input"
          />
          <button
            onClick={handleDeleteAccount}
            className="settings-button danger"
            disabled={deleting || deleteConfirm !== 'DELETE'}
          >
            {deleting ? 'Deleting...' : 'Delete Account'}
          </button>
        </div>
      </div>

    </div>
  );
}

// Help View Component
function HelpView() {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: 'How do I add an expense?',
      answer: 'Click the "+ Add Entry" button on the Dashboard, fill in the details, and optionally attach a receipt photo. You can also use AI scanning to automatically extract receipt information.'
    },
    {
      question: 'How does AI receipt scanning work?',
      answer: 'Take a photo of your receipt, and our AI will automatically extract the amount, date, vendor, and suggest a category. Basic plans include 10 scans per month, while Pro plans have unlimited scans.'
    },
    {
      question: 'How do I export my data?',
      answer: 'On the Dashboard, click the "Export" button to download a CSV file of all your entries for the selected year. This file can be opened in Excel, Google Sheets, or imported into tax software.'
    },
    {
      question: 'What\'s the difference between Basic and Pro?',
      answer: 'Both plans include unlimited manual entries and receipt uploads. Pro adds unlimited AI receipt scanning (vs 10/month on Basic) and priority support.'
    },
    {
      question: 'How do I cancel my subscription?',
      answer: 'Go to Settings > Manage Subscription to access the Stripe billing portal. From there, you can cancel your subscription. You\'ll retain access until the end of your billing period.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes. We use Supabase for secure data storage, Stripe for payment processing, and all connections are encrypted. We never share your financial data with third parties.'
    }
  ];

  return (
    <div className="help-view">
      <h1 className="view-title">Help & Support</h1>

      <div className="help-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div key={index} className={`faq-item ${openFaq === index ? 'open' : ''}`}>
              <button
                className="faq-question"
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              >
                <span>{faq.question}</span>
                <span className="faq-toggle">{openFaq === index ? '−' : '+'}</span>
              </button>
              {openFaq === index && (
                <div className="faq-answer">{faq.answer}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="help-section">
        <h2>Contact Support</h2>
        <p>Need help with something not covered above? Reach out to us.</p>
        <a href="mailto:support@creatorcents.app" className="help-email">
          support@creatorcents.app
        </a>
      </div>

      <div className="help-section">
        <h2>Legal</h2>
        <div className="help-links">
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>
        </div>
      </div>
    </div>
  );
}

// Dashboard View Component (the main tracker)
function DashboardView({
  user,
  isDemo,
  entries,
  setEntries,
  loading,
  setLoading,
  fetchExpenses,
  filterYear,
  setFilterYear,
  scanInfo,
  setScanInfo,
  subscription
}) {
  const [showForm, setShowForm] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [nextDemoId, setNextDemoId] = useState(11);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [mobileActionEntry, setMobileActionEntry] = useState(null); // For mobile entry action modal
  const exportDropdownRef = useRef(null);
  const mobileActionsRef = useRef(null);

  // Log Trip state
  const [showLogTrip, setShowLogTrip] = useState(false);
  const [tripData, setTripData] = useState({
    fromAddress: '',
    toAddress: '',
    miles: '',
    isRoundTrip: false,
    purpose: ''
  });
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcError, setCalcError] = useState(null);
  const [homeAddress, setHomeAddress] = useState(null);

  const isPro = subscription?.plan === 'pro_monthly' || subscription?.plan === 'pro_yearly';

  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);
  const scanInputRef = useRef(null);

  const [formData, setFormData] = useState({
    category: 'expense',
    subcategory: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    receiptPath: null,
    receiptName: '',
    isReimbursable: false,
    reimbursementStatus: 'pending',
    reimbursedBy: ''
  });

  // Fetch home address for Log Trip
  useEffect(() => {
    if (isDemo || !user) return;

    const fetchHomeAddress = async () => {
      const { data } = await supabase
        .from('saved_locations')
        .select('address')
        .eq('user_id', user.id)
        .eq('is_home', true)
        .maybeSingle(); // Use maybeSingle to avoid error when no home address exists

      if (data) {
        setHomeAddress(data.address);
        setTripData(prev => ({ ...prev, fromAddress: data.address }));
      }
    };

    fetchHomeAddress();
  }, [user, isDemo]);

  const resetForm = () => {
    setFormData({
      category: 'expense',
      subcategory: '',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      receiptPath: null,
      receiptName: '',
      isReimbursable: false,
      reimbursementStatus: 'pending',
      reimbursedBy: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  // Log Trip handlers
  const resetTripForm = () => {
    setTripData({
      fromAddress: homeAddress || '',
      toAddress: '',
      miles: '',
      isRoundTrip: false,
      purpose: ''
    });
    setCalcError(null);
    setShowLogTrip(false);
  };

  const handleCalculateDistance = async () => {
    if (!tripData.fromAddress || !tripData.toAddress) {
      setCalcError('Please enter both addresses');
      return;
    }

    setIsCalculating(true);
    setCalcError(null);

    try {
      const response = await fetch('/api/calculate-distance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          fromAddress: tripData.fromAddress,
          toAddress: tripData.toAddress,
          isRoundTrip: tripData.isRoundTrip
        })
      });

      const data = await response.json();

      if (data.error) {
        setCalcError(data.message || 'Could not calculate distance');
        return;
      }

      if (data.success) {
        setTripData(prev => ({
          ...prev,
          miles: data.miles.toString(),
          fromAddress: data.fromAddress,
          toAddress: data.toAddress
        }));
      }
    } catch (err) {
      console.error('Error calculating distance:', err);
      setCalcError('Failed to calculate distance. Please try again.');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleLogTrip = async (e) => {
    e.preventDefault();

    const miles = parseFloat(tripData.miles);
    if (!miles || miles <= 0) {
      setCalcError('Please enter valid miles');
      return;
    }

    try {
      const entry = {
        category: 'miles',
        amount: miles,
        description: tripData.purpose || `Trip: ${tripData.fromAddress} to ${tripData.toAddress}`.substring(0, 200),
        expense_date: new Date().toISOString().split('T')[0],
        receipt_path: null,
        receipt_name: ''
      };

      if (isDemo) {
        const newEntry = {
          ...entry,
          id: `demo-${nextDemoId}`,
          date: entry.expense_date
        };
        setEntries([newEntry, ...entries]);
        setNextDemoId(nextDemoId + 1);
      } else {
        // Save to expenses table
        const { error } = await supabase
          .from('expenses')
          .insert({ ...entry, user_id: user.id });

        if (error) throw error;

        // For Pro users, also save detailed mileage entry
        if (isPro) {
          await supabase
            .from('mileage_entries')
            .insert({
              user_id: user.id,
              date: entry.expense_date,
              from_address: tripData.fromAddress,
              to_address: tripData.toAddress,
              miles: miles,
              is_round_trip: tripData.isRoundTrip,
              purpose: tripData.purpose
            });
        }

        await fetchExpenses();
      }

      resetTripForm();
    } catch (err) {
      console.error('Error logging trip:', err);
      alert('Failed to log trip. Please try again.');
    }
  };

  const IRS_RATE = 0.67;

  // Compress image to max ~1MB
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          const maxDim = 1200;
          if (width > height && width > maxDim) {
            height = (height * maxDim) / width;
            width = maxDim;
          } else if (height > maxDim) {
            width = (width * maxDim) / height;
            height = maxDim;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          let quality = 0.8;
          let base64 = canvas.toDataURL('image/jpeg', quality);

          while (base64.length > 1400000 && quality > 0.3) {
            quality -= 0.1;
            base64 = canvas.toDataURL('image/jpeg', quality);
          }

          resolve(base64);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle receipt scanning
  const handleScanReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (scanInputRef.current) {
      scanInputRef.current.value = '';
    }

    setScanError(null);
    setIsScanning(true);

    try {
      if (isDemo) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        setFormData({
          ...formData,
          amount: '24.99',
          date: new Date().toISOString().split('T')[0],
          description: 'Demo Coffee Shop',
          category: 'food'
        });
        setIsScanning(false);
        return;
      }

      if (!scanInfo.isPro && scanInfo.scansUsed >= scanInfo.scansAllowed) {
        setScanError('limit_reached');
        setIsScanning(false);
        return;
      }

      const compressedBase64 = await compressImage(file);

      const response = await fetch('/api/scan-receipt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageBase64: compressedBase64,
          userId: user.id
        })
      });

      const data = await response.json();

      if (data.scansUsed !== undefined) {
        setScanInfo(prev => ({
          ...prev,
          scansUsed: data.scansUsed,
          scansAllowed: data.scansAllowed === 'unlimited' ? 'unlimited' : data.scansAllowed
        }));
      }

      if (data.error) {
        if (data.error === 'limit_reached') {
          setScanError('limit_reached');
        } else if (data.error === 'unreadable' || data.error === 'parse_failed') {
          setScanError("Couldn't read receipt. Please enter manually.");
        } else if (data.error === 'timeout') {
          setScanError('Scan timed out. Please try again or enter manually.');
        } else {
          setScanError('Connection error. Please try again.');
        }
        setIsScanning(false);
        return;
      }

      if (data.success) {
        setFormData({
          ...formData,
          amount: data.amount?.toString() || '',
          date: data.date || new Date().toISOString().split('T')[0],
          description: data.vendor || '',
          category: data.suggestedCategory || 'expense',
          subcategory: data.suggestedSubcategory || ''
        });

        if (file.size <= 10 * 1024 * 1024) {
          const fileExt = file.name.split('.').pop();
          const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('receipts')
            .upload(fileName, file);

          if (!uploadError && uploadData) {
            setFormData(prev => ({
              ...prev,
              receiptPath: uploadData.path,
              receiptName: file.name
            }));
          }
        }
      }

    } catch (err) {
      console.error('Scan error:', err);
      setScanError('Connection error. Please try again.');
    } finally {
      setIsScanning(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      if (isDemo) {
        setFormData({
          ...formData,
          receiptPath: 'demo-receipt-path',
          receiptName: file.name
        });
        return;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('receipts')
        .upload(fileName, file);

      if (error) throw error;

      setFormData({
        ...formData,
        receiptPath: data.path,
        receiptName: file.name
      });
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if this is an expense category (not income)
    const isExpenseCategory = formData.category !== 'income';

    const entry = {
      category: formData.category,
      subcategory: formData.category === 'expense' ? (formData.subcategory || null) : null,
      amount: parseFloat(formData.amount),
      description: formData.description,
      expense_date: formData.date,
      receipt_path: formData.receiptPath,
      receipt_name: formData.receiptName,
      is_reimbursable: isExpenseCategory ? formData.isReimbursable : false,
      reimbursement_status: isExpenseCategory && formData.isReimbursable ? formData.reimbursementStatus : null,
      reimbursed_by: isExpenseCategory && formData.isReimbursable ? (formData.reimbursedBy || null) : null
    };

    try {
      if (isDemo) {
        if (editingId) {
          setEntries(entries.map(e =>
            e.id === editingId
              ? { ...entry, id: editingId, date: entry.expense_date, receiptPath: entry.receipt_path, receiptName: entry.receipt_name, isReimbursable: entry.is_reimbursable, reimbursementStatus: entry.reimbursement_status, reimbursedBy: entry.reimbursed_by }
              : e
          ));
        } else {
          const newEntry = {
            ...entry,
            id: `demo-${nextDemoId}`,
            date: entry.expense_date,
            receiptPath: entry.receipt_path,
            receiptName: entry.receipt_name,
            isReimbursable: entry.is_reimbursable,
            reimbursementStatus: entry.reimbursement_status,
            reimbursedBy: entry.reimbursed_by
          };
          setEntries([newEntry, ...entries]);
          setNextDemoId(nextDemoId + 1);
        }
        resetForm();
        return;
      }

      if (editingId) {
        const { error } = await supabase
          .from('expenses')
          .update(entry)
          .eq('id', editingId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('expenses')
          .insert({ ...entry, user_id: user.id });

        if (error) throw error;
      }

      await fetchExpenses();
      resetForm();
    } catch (err) {
      console.error('Error saving:', err);
      alert('Failed to save. Please try again.');
    }
  };

  const handleEdit = (entry) => {
    setFormData({
      category: entry.category,
      subcategory: entry.subcategory || '',
      amount: entry.amount.toString(),
      description: entry.description,
      date: entry.date,
      receiptPath: entry.receiptPath,
      receiptName: entry.receiptName || '',
      isReimbursable: entry.isReimbursable || false,
      reimbursementStatus: entry.reimbursementStatus || 'pending',
      reimbursedBy: entry.reimbursedBy || ''
    });
    setEditingId(entry.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;

    try {
      if (isDemo) {
        setEntries(entries.filter(e => e.id !== id));
        return;
      }

      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchExpenses();
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Failed to delete. Please try again.');
    }
  };

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };

    if (showExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportDropdown]);

  // Close mobile actions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileActionsRef.current && !mobileActionsRef.current.contains(event.target)) {
        setShowMobileActions(false);
      }
    };

    if (showMobileActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileActions]);

  const exportToCSV = () => {
    if (entries.length === 0) {
      alert('No entries to export for this year.');
      return;
    }

    const headers = ['Date', 'Category', 'Subcategory', 'Amount', 'Description', 'Has Receipt'];
    const rows = entries.map(e => {
      const cat = CATEGORIES.find(c => c.id === e.category);
      const subcat = e.subcategory ? EXPENSE_SUBCATEGORIES.find(s => s.id === e.subcategory)?.label : '';
      return [
        e.date,
        cat?.label || e.category,
        subcat || '',
        e.amount.toFixed(2),
        `"${(e.description || '').replace(/"/g, '""')}"`,
        e.receiptPath ? 'Yes' : 'No'
      ];
    });

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `creatorcents-${filterYear}.csv`;
    a.click();
    setShowExportDropdown(false);
  };

  const exportToExcel = () => {
    if (entries.length === 0) {
      alert('No entries to export for this year.');
      return;
    }

    const data = entries.map(e => {
      const cat = CATEGORIES.find(c => c.id === e.category);
      const subcat = e.subcategory ? EXPENSE_SUBCATEGORIES.find(s => s.id === e.subcategory)?.label : '';
      return {
        'Date': e.date,
        'Category': cat?.label || e.category,
        'Subcategory': subcat || '',
        'Amount': e.amount,
        'Description': e.description || '',
        'Has Receipt': e.receiptPath ? 'Yes' : 'No'
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Expenses');

    // Auto-size columns
    const colWidths = [
      { wch: 12 }, // Date
      { wch: 18 }, // Category
      { wch: 15 }, // Subcategory
      { wch: 12 }, // Amount
      { wch: 40 }, // Description
      { wch: 12 }  // Has Receipt
    ];
    worksheet['!cols'] = colWidths;

    XLSX.writeFile(workbook, `creatorcents-${filterYear}.xlsx`);
    setShowExportDropdown(false);
  };

  const exportToPDF = () => {
    if (entries.length === 0) {
      alert('No entries to export for this year.');
      return;
    }

    const doc = new jsPDF();

    // Title
    doc.setFontSize(20);
    doc.setTextColor(51, 51, 51);
    doc.text('CreatorCents Expense Report', 14, 22);

    // Subtitle with year
    doc.setFontSize(12);
    doc.setTextColor(102, 102, 102);
    doc.text(`Year: ${filterYear}`, 14, 32);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 40);

    // Summary section
    doc.setFontSize(14);
    doc.setTextColor(51, 51, 51);
    doc.text('Summary', 14, 55);

    const totalIncome = entries
      .filter(e => CATEGORIES.find(c => c.id === e.category)?.isIncome)
      .reduce((sum, e) => sum + e.amount, 0);

    const totalExpenses = entries
      .filter(e => {
        const cat = CATEGORIES.find(c => c.id === e.category);
        return cat && !cat.isIncome && cat.affectsTotals;
      })
      .reduce((sum, e) => sum + e.amount, 0);

    doc.setFontSize(11);
    doc.text(`Total Income: ${formatCurrency(totalIncome)}`, 14, 65);
    doc.text(`Total Expenses: ${formatCurrency(totalExpenses)}`, 14, 72);
    doc.text(`Net Profit: ${formatCurrency(totalIncome - totalExpenses)}`, 14, 79);

    // Table data
    const tableData = entries.map(e => {
      const cat = CATEGORIES.find(c => c.id === e.category);
      const subcat = e.subcategory ? EXPENSE_SUBCATEGORIES.find(s => s.id === e.subcategory)?.label : '';
      return [
        e.date,
        cat?.label || e.category,
        subcat || '-',
        `$${e.amount.toFixed(2)}`,
        (e.description || '').substring(0, 30) + ((e.description || '').length > 30 ? '...' : '')
      ];
    });

    doc.autoTable({
      startY: 90,
      head: [['Date', 'Category', 'Subcategory', 'Amount', 'Description']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [99, 102, 241],
        textColor: 255,
        fontStyle: 'bold'
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 30 },
        2: { cellWidth: 25 },
        3: { cellWidth: 25, halign: 'right' },
        4: { cellWidth: 'auto' }
      }
    });

    doc.save(`creatorcents-${filterYear}.pdf`);
    setShowExportDropdown(false);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          alert('CSV file is empty or has no data rows.');
          return;
        }

        const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
        const dateIdx = headers.findIndex(h => h.includes('date'));
        const categoryIdx = headers.findIndex(h => h.includes('category'));
        const amountIdx = headers.findIndex(h => h.includes('amount'));
        const descIdx = headers.findIndex(h => h.includes('description') || h.includes('desc'));

        if (dateIdx === -1 || amountIdx === -1) {
          alert('CSV must have Date and Amount columns.');
          return;
        }

        const newEntries = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));

          const amount = parseFloat(values[amountIdx]);
          if (isNaN(amount)) continue;

          let category = 'expense';
          if (categoryIdx !== -1 && values[categoryIdx]) {
            const catValue = values[categoryIdx].toLowerCase();
            const foundCat = CATEGORIES.find(c =>
              c.label.toLowerCase() === catValue || c.id === catValue
            );
            if (foundCat) category = foundCat.id;
          }

          newEntries.push({
            user_id: user?.id,
            category,
            amount,
            description: descIdx !== -1 ? values[descIdx] : '',
            expense_date: values[dateIdx] || new Date().toISOString().split('T')[0]
          });
        }

        if (newEntries.length === 0) {
          alert('No valid entries found in CSV.');
          return;
        }

        if (!window.confirm(`Import ${newEntries.length} entries?`)) {
          return;
        }

        if (isDemo) {
          const importedEntries = newEntries.map((entry, index) => ({
            id: `demo-import-${nextDemoId + index}`,
            category: entry.category,
            amount: entry.amount,
            description: entry.description || '',
            date: entry.expense_date,
            receiptPath: null,
            receiptName: ''
          }));
          setEntries([...importedEntries, ...entries]);
          setNextDemoId(nextDemoId + newEntries.length);
          alert(`Successfully imported ${newEntries.length} entries!`);
          return;
        }

        const { error } = await supabase
          .from('expenses')
          .insert(newEntries);

        if (error) throw error;

        alert(`Successfully imported ${newEntries.length} entries!`);
        await fetchExpenses();

      } catch (err) {
        console.error('Import error:', err);
        alert('Failed to import: ' + err.message);
      }
    };

    reader.readAsText(file);
    e.target.value = '';
  };

  const getFilteredAndSortedEntries = () => {
    let result = [...entries];

    if (filterCategory !== 'all') {
      result = result.filter(e => e.category === filterCategory);
    }

    switch (sortBy) {
      case 'date-desc':
        result.sort((a, b) => new Date(b.date) - new Date(a.date));
        break;
      case 'date-asc':
        result.sort((a, b) => new Date(a.date) - new Date(b.date));
        break;
      case 'amount-desc':
        result.sort((a, b) => b.amount - a.amount);
        break;
      case 'amount-asc':
        result.sort((a, b) => a.amount - b.amount);
        break;
      case 'category':
        result.sort((a, b) => a.category.localeCompare(b.category));
        break;
      default:
        break;
    }

    return result;
  };

  const filteredEntries = getFilteredAndSortedEntries();
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - 2 + i).toString());

  // Calculate totals, excluding reimbursed expenses from expense totals
  const totals = CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = entries
      .filter(e => e.category === cat.id)
      // Exclude reimbursed expenses from totals (they don't affect tax deductions)
      .filter(e => !(e.isReimbursable && e.reimbursementStatus === 'reimbursed'))
      .reduce((sum, e) => sum + e.amount, 0);
    return acc;
  }, {});

  const totalIncome = CATEGORIES
    .filter(cat => cat.isIncome)
    .reduce((sum, cat) => sum + (totals[cat.id] || 0), 0);

  const totalExpenses = CATEGORIES
    .filter(cat => cat.affectsTotals && !cat.isIncome)
    .reduce((sum, cat) => sum + (totals[cat.id] || 0), 0);

  // Calculate pending reimbursements
  const pendingReimbursements = entries
    .filter(e => e.isReimbursable && e.reimbursementStatus === 'pending')
    .reduce((sum, e) => sum + e.amount, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (loading && entries.length === 0) {
    return (
      <div className="dashboard-loading">
        <div className="loading-text">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-view">
      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchExpenses} className="retry-button">Retry</button>
        </div>
      )}

      <div className="dashboard-header">
        <div>
          <h1 className="view-title">Dashboard</h1>
          <p className="view-subtitle">Track your income and expenses</p>
        </div>
        <div className="dashboard-actions">
          <div className="year-filter">
            <label>Year:</label>
            <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Quick Category Filter with Amounts */}
      <div className="quick-category-filter">
        <button
          className={`category-chip ${filterCategory === 'all' ? 'active' : ''}`}
          onClick={() => setFilterCategory('all')}
        >
          <span className="category-chip-label">All</span>
        </button>
        {CATEGORIES.map(cat => {
          const amount = totals[cat.id] || 0;
          return (
            <button
              key={cat.id}
              className={`category-chip with-amount ${filterCategory === cat.id ? 'active' : ''} ${amount === 0 ? 'empty' : ''}`}
              onClick={() => setFilterCategory(cat.id)}
            >
              <div className="category-chip-top">
                <span className="category-chip-icon">{cat.icon}</span>
                <span className="category-chip-name">{cat.label}</span>
              </div>
              <div className="category-chip-amount">
                {cat.id === 'miles' ? `${amount.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mi` : formatCurrency(amount)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Summary Cards - 2 Column Layout */}
      <div className="summary-cards-grid">
        {/* Left column - stacked Income/Expenses */}
        <div className="summary-cards-left">
          <div className="summary-card income">
            <div className="summary-label">Total Income</div>
            <div className="summary-amount">{formatCurrency(totalIncome)}</div>
          </div>
          <div className="summary-card expenses">
            <div className="summary-label">Total Expenses</div>
            <div className="summary-amount">{formatCurrency(totalExpenses)}</div>
          </div>
        </div>

        {/* Right column - Net Profit (full height, featured) */}
        <div className={`summary-card-net-profit ${totalIncome - totalExpenses >= 0 ? 'profit' : 'loss'}`}>
          <div className="summary-label">Net Profit</div>
          <div className="summary-amount">{formatCurrency(totalIncome - totalExpenses)}</div>
          {/* Decorative arrow */}
          <svg className="net-profit-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M7 17L17 7M17 7H7M17 7V17" />
          </svg>
          {/* Decorative glow effect */}
          <div className="net-profit-glow"></div>
        </div>
      </div>

      {/* Pending Reimbursements Banner */}
      {pendingReimbursements > 0 && (
        <div className="pending-reimbursements-banner">
          <span className="pending-icon">🔄</span>
          <span>Pending Reimbursements: {formatCurrency(pendingReimbursements)}</span>
        </div>
      )}

      {/* Controls */}
      <div className="controls">
        <div className="control-buttons">
          <div className="export-dropdown-container" ref={exportDropdownRef}>
            <button
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              className="control-button"
              title="Export data"
            >
              📥 Export ▾
            </button>
            {showExportDropdown && (
              <div className="export-dropdown-menu">
                <button onClick={exportToCSV} className="export-dropdown-item">
                  <span className="export-icon">📄</span>
                  <div className="export-item-content">
                    <span className="export-item-label">CSV</span>
                    <span className="export-item-desc">Universal format</span>
                  </div>
                </button>
                <button onClick={exportToExcel} className="export-dropdown-item">
                  <span className="export-icon">📊</span>
                  <div className="export-item-content">
                    <span className="export-item-label">Excel</span>
                    <span className="export-item-desc">Spreadsheet format</span>
                  </div>
                </button>
                <button onClick={exportToPDF} className="export-dropdown-item">
                  <span className="export-icon">📑</span>
                  <div className="export-item-content">
                    <span className="export-item-label">PDF Report</span>
                    <span className="export-item-desc">Professional report</span>
                  </div>
                </button>
              </div>
            )}
          </div>
          <button onClick={() => importInputRef.current?.click()} className="control-button" title="Import from CSV">
            📤 Import
          </button>
          <input
            type="file"
            ref={importInputRef}
            onChange={handleImport}
            accept=".csv"
            style={{ display: 'none' }}
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`control-button ${showFilters ? 'active' : ''}`}
          >
            🔍 Filter
          </button>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="control-button primary">
            + Add Entry
          </button>
        </div>

      </div>

      {/* Floating Action Buttons - Mobile Only */}
      <div className="mobile-controls" ref={mobileActionsRef}>
        <button
          className={`mobile-gear-button ${showMobileActions ? 'active' : ''}`}
          onClick={() => setShowMobileActions(!showMobileActions)}
          aria-label="Actions menu"
        >
          ⚙️
        </button>
        <button
          className="mobile-fab-button"
          onClick={() => { resetForm(); setShowForm(true); }}
          aria-label="Add entry"
        >
          +
        </button>
        {showMobileActions && (
          <div className="mobile-actions-menu">
            <button
              className="mobile-actions-item"
              onClick={() => { setShowLogTrip(true); setShowMobileActions(false); }}
            >
              <span className="mobile-actions-item-icon">🚗</span>
              <span>Log Trip</span>
            </button>
            <div className="mobile-actions-divider"></div>
            <button
              className="mobile-actions-item"
              onClick={() => { exportToCSV(); setShowMobileActions(false); }}
            >
              <span className="mobile-actions-item-icon">📄</span>
              <span>Export CSV</span>
            </button>
            <button
              className="mobile-actions-item"
              onClick={() => { exportToExcel(); setShowMobileActions(false); }}
            >
              <span className="mobile-actions-item-icon">📊</span>
              <span>Export Excel</span>
            </button>
            <button
              className="mobile-actions-item"
              onClick={() => { exportToPDF(); setShowMobileActions(false); }}
            >
              <span className="mobile-actions-item-icon">📑</span>
              <span>Export PDF</span>
            </button>
            <div className="mobile-actions-divider"></div>
            <button
              className="mobile-actions-item"
              onClick={() => { importInputRef.current?.click(); setShowMobileActions(false); }}
            >
              <span className="mobile-actions-item-icon">📤</span>
              <span>Import CSV</span>
            </button>
            <button
              className="mobile-actions-item"
              onClick={() => { setShowFilters(!showFilters); setShowMobileActions(false); }}
            >
              <span className="mobile-actions-item-icon">🔍</span>
              <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
            </button>
          </div>
        )}
      </div>

      {showFilters && (
        <div className="filter-panel">
          <div className="filter-group">
            <label>Category:</label>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="all">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Sort:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="date-desc">Date (Newest)</option>
              <option value="date-asc">Date (Oldest)</option>
              <option value="amount-desc">Amount (High)</option>
              <option value="amount-asc">Amount (Low)</option>
              <option value="category">Category</option>
            </select>
          </div>
        </div>
      )}

      {/* Entries List */}
      <div className="entries-section">
        <h2 className="entries-title">
          Entries ({filteredEntries.length})
        </h2>

        {filteredEntries.length === 0 ? (
          <div className="entries-empty">
            <p>No entries yet for {filterYear}</p>
            <button onClick={() => { resetForm(); setShowForm(true); }} className="empty-add-button">
              Add Your First Entry
            </button>
          </div>
        ) : (
          <>
            {/* Mobile: Table Layout */}
            <div className="entries-mobile-table">
              <div className="entries-table-wrapper">
                <div className="entries-table">
                  <div className="entries-table-header">
                    <span className="col-date">Date</span>
                    <span className="col-desc">Description</span>
                    <span className="col-category">Category</span>
                    <span className="col-amount">Amount</span>
                  </div>
                  <div className="entries-table-body">
                    {filteredEntries.map(entry => {
                      const cat = CATEGORIES.find(c => c.id === entry.category);
                      const dateObj = new Date(entry.date + 'T00:00:00');
                      const monthStr = dateObj.toLocaleDateString('en-US', { month: 'short' });
                      const dayStr = dateObj.getDate();
                      const yearStr = dateObj.getFullYear();

                      const getCategoryBadgeClass = (categoryId) => {
                        switch(categoryId) {
                          case 'income': return 'badge-income';
                          case 'expense': return 'badge-business';
                          case 'miles': return 'badge-miles';
                          case 'fuel': return 'badge-fuel';
                          case 'lodging': return 'badge-lodging';
                          case 'food': return 'badge-food';
                          default: return 'badge-default';
                        }
                      };

                      return (
                        <div
                          key={entry.id}
                          className="entry-row"
                          onClick={() => setMobileActionEntry(entry)}
                        >
                          <div className="col-date entry-date-stacked">
                            <span>{monthStr}</span>
                            <span>{dayStr},</span>
                            <span>{yearStr}</span>
                          </div>
                          <div className="col-desc entry-desc">
                            {entry.description || cat?.label}
                            {entry.receiptPath && <span className="receipt-indicator">📎</span>}
                          </div>
                          <div className="col-category">
                            <span className={`category-badge ${getCategoryBadgeClass(entry.category)}`}>
                              {cat?.label || entry.category}
                            </span>
                          </div>
                          <div className={`col-amount entry-amount ${entry.category === 'miles' ? 'miles' : (cat?.isIncome ? 'income' : 'expense')}`}>
                            {entry.category === 'miles'
                              ? `${entry.amount.toFixed(1)} mi`
                              : `${cat?.isIncome ? '+' : '-'}${formatCurrency(entry.amount)}`
                            }
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop: Original Card Layout */}
            <div className="entries-desktop-list">
              <div className="entries">
                {filteredEntries.map(entry => {
                  const cat = CATEGORIES.find(c => c.id === entry.category);
                  return (
                    <div key={entry.id} className="entry">
                      <div className="entry-left">
                        <span className="entry-icon">{cat?.icon}</span>
                        <div>
                          <div className="entry-amount">
                            <span className={entry.category === 'miles' ? 'miles' : (cat?.isIncome ? 'income' : 'expense')}>
                              {entry.category === 'miles'
                                ? `${entry.amount.toFixed(1)} mi`
                                : `${cat?.isIncome ? '+' : '-'}${formatCurrency(entry.amount)}`
                              }
                            </span>
                            <span className={`entry-badge ${cat?.isIncome ? 'income' : ''}`}>
                              {cat?.label}
                            </span>
                            {entry.receiptPath && (
                              <a href={entry.receiptPath} target="_blank" rel="noopener noreferrer" className="receipt-badge">
                                📎
                              </a>
                            )}
                          </div>
                          <div className="entry-meta">
                            <span className="entry-date">{new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            {entry.description && <span className="entry-description"> • {entry.description}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="entry-actions-desktop">
                        <button onClick={() => handleEdit(entry)} className="entry-edit">Edit</button>
                        <button onClick={() => handleDelete(entry.id)} className="entry-delete">Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => resetForm()}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Entry' : 'Add Entry'}</h2>
              <button onClick={() => resetForm()} className="modal-close">×</button>
            </div>

            <form onSubmit={handleSubmit} className="entry-form">
              {/* AI Scan Button */}
              {!editingId && (
                <div className="scan-section">
                  <button
                    type="button"
                    onClick={() => scanInputRef.current?.click()}
                    disabled={isScanning}
                    className="scan-button"
                  >
                    {isScanning ? '🔄 Scanning...' : '📷 Scan Receipt with AI'}
                  </button>
                  <input
                    type="file"
                    ref={scanInputRef}
                    onChange={handleScanReceipt}
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                  />
                  {!isDemo && (
                    <div className="scan-info">
                      {scanInfo.isPro
                        ? 'Unlimited scans'
                        : `${scanInfo.scansUsed}/${scanInfo.scansAllowed} scans used this month`
                      }
                    </div>
                  )}
                  {scanError && (
                    <div className="scan-error">
                      {scanError === 'limit_reached'
                        ? 'Monthly scan limit reached. Pro plan includes unlimited scans.'
                        : scanError
                      }
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value, subcategory: '' })}
                  required
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                  ))}
                </select>
              </div>

              {formData.category === 'expense' && (
                <div className="form-group">
                  <label>Subcategory (optional)</label>
                  <select
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  >
                    <option value="">Select subcategory...</option>
                    {EXPENSE_SUBCATEGORIES.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>{formData.category === 'miles' ? 'Miles' : 'Amount'}</label>
                <input
                  type="number"
                  step={formData.category === 'miles' ? '0.1' : '0.01'}
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder={formData.category === 'miles' ? '0.0' : '0.00'}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What was this for?"
                />
              </div>

              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>

              {/* Reimbursement fields - only for expense categories */}
              {formData.category !== 'income' && (
                <div className="reimbursement-section">
                  <label className="reimbursable-toggle">
                    <input
                      type="checkbox"
                      checked={formData.isReimbursable}
                      onChange={(e) => setFormData({
                        ...formData,
                        isReimbursable: e.target.checked,
                        reimbursementStatus: e.target.checked ? 'pending' : 'pending',
                        reimbursedBy: e.target.checked ? formData.reimbursedBy : ''
                      })}
                    />
                    <span>Reimbursable expense</span>
                  </label>

                  {formData.isReimbursable && (
                    <div className="reimbursement-fields">
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          value={formData.reimbursementStatus}
                          onChange={(e) => setFormData({ ...formData, reimbursementStatus: e.target.value })}
                        >
                          <option value="pending">Pending</option>
                          <option value="reimbursed">Reimbursed</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Reimbursed By (optional)</label>
                        <input
                          type="text"
                          value={formData.reimbursedBy}
                          onChange={(e) => setFormData({ ...formData, reimbursedBy: e.target.value })}
                          placeholder="e.g., Campbell Agency, Nike"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Receipt (optional)</label>
                <div className="receipt-upload">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="upload-button"
                  >
                    {uploading ? 'Uploading...' : formData.receiptName || 'Choose File'}
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/*,.pdf"
                    style={{ display: 'none' }}
                  />
                  {formData.receiptName && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, receiptPath: null, receiptName: '' })}
                      className="remove-receipt"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => resetForm()} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="submit-button">
                  {editingId ? 'Update' : 'Add Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Entry Action Modal */}
      {mobileActionEntry && (
        <div className="entry-action-modal-overlay" onClick={() => setMobileActionEntry(null)}>
          <div className="entry-action-modal" onClick={e => e.stopPropagation()}>
            <div className="entry-action-modal-header">
              <p className="entry-action-modal-title">
                {mobileActionEntry.description || CATEGORIES.find(c => c.id === mobileActionEntry.category)?.label}
              </p>
              <p className="entry-action-modal-subtitle">
                {mobileActionEntry.date} • {mobileActionEntry.category === 'miles' ? `${mobileActionEntry.amount.toFixed(1)} mi` : formatCurrency(mobileActionEntry.amount)}
              </p>
            </div>
            <div className="entry-action-modal-actions">
              <button
                className="entry-action-button"
                onClick={() => {
                  handleEdit(mobileActionEntry);
                  setMobileActionEntry(null);
                }}
              >
                <span className="entry-action-button-icon">✏️</span>
                Edit Entry
              </button>
              <button
                className="entry-action-button delete"
                onClick={() => {
                  if (window.confirm('Delete this entry?')) {
                    handleDelete(mobileActionEntry.id);
                    setMobileActionEntry(null);
                  }
                }}
              >
                <span className="entry-action-button-icon">🗑️</span>
                Delete Entry
              </button>
            </div>
            <button className="entry-action-cancel" onClick={() => setMobileActionEntry(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Log Trip Modal */}
      {showLogTrip && (
        <div className="modal-overlay" onClick={resetTripForm}>
          <div className="modal log-trip-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Log Trip</h2>
              <button onClick={resetTripForm} className="modal-close">×</button>
            </div>

            <form onSubmit={handleLogTrip} className="log-trip-form">
              {calcError && <div className="auth-error">{calcError}</div>}

              <div className="form-group">
                <label>From</label>
                <div className="address-input-group">
                  {isPro ? (
                    <AddressAutocomplete
                      value={tripData.fromAddress}
                      onChange={(addr) => setTripData({ ...tripData, fromAddress: addr })}
                      placeholder="Starting address"
                      disabled={false}
                      className=""
                    />
                  ) : (
                    <input
                      type="text"
                      value={tripData.fromAddress}
                      onChange={(e) => setTripData({ ...tripData, fromAddress: e.target.value })}
                      placeholder="Pro feature"
                      disabled={true}
                      className="disabled-input"
                    />
                  )}
                  {isPro && homeAddress && tripData.fromAddress !== homeAddress && (
                    <button
                      type="button"
                      className="use-home-btn"
                      onClick={() => setTripData({ ...tripData, fromAddress: homeAddress })}
                    >
                      Use Home
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>To</label>
                {isPro ? (
                  <AddressAutocomplete
                    value={tripData.toAddress}
                    onChange={(addr) => setTripData({ ...tripData, toAddress: addr })}
                    placeholder="Destination address"
                    disabled={false}
                    className=""
                  />
                ) : (
                  <input
                    type="text"
                    value={tripData.toAddress}
                    onChange={(e) => setTripData({ ...tripData, toAddress: e.target.value })}
                    placeholder="Pro feature"
                    disabled={true}
                    className="disabled-input"
                  />
                )}
              </div>

              {isPro && (
                <label className="round-trip-toggle">
                  <input
                    type="checkbox"
                    checked={tripData.isRoundTrip}
                    onChange={(e) => setTripData({ ...tripData, isRoundTrip: e.target.checked, miles: '' })}
                  />
                  <span>Round trip (doubles miles)</span>
                </label>
              )}

              {isPro && (
                <button
                  type="button"
                  onClick={handleCalculateDistance}
                  disabled={isCalculating || !tripData.fromAddress || !tripData.toAddress}
                  className="calculate-btn"
                >
                  {isCalculating ? 'Calculating...' : 'Calculate Miles'}
                </button>
              )}

              <div className="form-group">
                <label>Miles {!isPro && '(enter manually)'}</label>
                <input
                  type="number"
                  step="0.1"
                  value={tripData.miles}
                  onChange={(e) => setTripData({ ...tripData, miles: e.target.value })}
                  placeholder="0.0"
                  required
                />
              </div>

              {tripData.miles && parseFloat(tripData.miles) > 0 && (
                <div className="deduction-estimate">
                  <span className="estimate-label">Estimated Deduction</span>
                  <span className="estimate-value">
                    {parseFloat(tripData.miles).toFixed(1)} mi × ${IRS_RATE} = ${(parseFloat(tripData.miles) * IRS_RATE).toFixed(2)}
                  </span>
                </div>
              )}

              <div className="form-group">
                <label>Business Purpose (optional)</label>
                <input
                  type="text"
                  value={tripData.purpose}
                  onChange={(e) => setTripData({ ...tripData, purpose: e.target.value })}
                  placeholder="e.g., Client meeting, Studio filming"
                />
              </div>

              {!isPro && (
                <div className="pro-upgrade-prompt">
                  <p className="upgrade-title">Pro Feature</p>
                  <ul className="upgrade-features">
                    <li>Automatic distance calculation</li>
                    <li>Round trip tracking</li>
                    <li>Saved routes (coming soon)</li>
                  </ul>
                  <p className="upgrade-hint">Go to Settings → Manage Subscription to upgrade</p>
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={resetTripForm} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="submit-button" disabled={!tripData.miles || parseFloat(tripData.miles) <= 0}>
                  Log Trip
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Main App Layout with Sidebar
function AppLayout({ user, subscription, isDemo, onExitDemo, onSignOut }) {
  const [activeView, setActiveView] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [entries, setEntries] = useState(isDemo ? DEMO_DATA : []);
  const [loading, setLoading] = useState(!isDemo);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [scanInfo, setScanInfo] = useState({
    scansUsed: isDemo ? 10 : 0,
    scansAllowed: isDemo ? 10 : 10,
    isPro: false
  });

  // Fetch expenses
  const fetchExpenses = async () => {
    if (isDemo) return;

    try {
      setLoading(true);

      const startDate = `${filterYear}-01-01`;
      const endDate = `${filterYear}-12-31`;

      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .eq('user_id', user.id)
        .gte('expense_date', startDate)
        .lte('expense_date', endDate)
        .order('expense_date', { ascending: false });

      if (error) throw error;

      // Debug logging
      console.log('[fetchExpenses] Year:', filterYear);
      console.log('[fetchExpenses] Total entries fetched:', data?.length || 0);
      const withReceipts = data?.filter(row => row.receipt_path) || [];
      console.log('[fetchExpenses] Entries with receipt_path:', withReceipts.length);
      if (withReceipts.length > 0) {
        console.log('[fetchExpenses] Sample receipt paths:', withReceipts.slice(0, 3).map(r => ({
          id: r.id,
          receipt_path: r.receipt_path,
          date: r.expense_date
        })));
      }

      const mapped = data.map(row => ({
        id: row.id,
        category: normalizeCategory(row.category),
        subcategory: row.subcategory || null,
        amount: parseFloat(row.amount),
        description: row.description,
        date: row.expense_date,
        receiptPath: row.receipt_path,
        receiptName: row.receipt_name,
        isReimbursable: row.is_reimbursable || false,
        reimbursementStatus: row.reimbursement_status || null,
        reimbursedBy: row.reimbursed_by || null
      }));

      setEntries(mapped);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch scan info
  const fetchScanInfo = async () => {
    if (isDemo) {
      setScanInfo({ scansUsed: 10, scansAllowed: 10, isPro: false });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('plan, scans_used, scans_reset_date')
        .eq('user_id', user.id)
        .single();

      if (error) return;

      const isPro = data.plan === 'pro_monthly' || data.plan === 'pro_yearly';
      setScanInfo({
        scansUsed: data.scans_used || 0,
        scansAllowed: isPro ? 'unlimited' : 10,
        isPro
      });
    } catch (err) {
      console.error('Error fetching scan info:', err);
    }
  };

  useEffect(() => {
    if (!isDemo) {
      fetchExpenses();
      fetchScanInfo();
    }
  }, [filterYear, isDemo]);

  // Reset scroll position when tab/view changes
  useEffect(() => {
    // Scroll the window to top
    window.scrollTo(0, 0);

    // Also scroll the main-content container to top (for mobile)
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }

    // Also try scrolling the specific view containers
    const viewContainers = document.querySelectorAll('.dashboard-view, .reports-view, .receipts-view, .settings-view, .help-view');
    viewContainers.forEach(container => {
      if (container) {
        container.scrollTop = 0;
      }
    });
  }, [activeView]);

  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <DashboardView
            user={user}
            isDemo={isDemo}
            entries={entries}
            setEntries={setEntries}
            loading={loading}
            setLoading={setLoading}
            fetchExpenses={fetchExpenses}
            filterYear={filterYear}
            setFilterYear={setFilterYear}
            scanInfo={scanInfo}
            setScanInfo={setScanInfo}
            subscription={subscription}
          />
        );
      case 'reports':
        return <ReportsView user={user} isDemo={isDemo} entries={entries} />;
      case 'receipts':
        return <ReceiptsView entries={entries} user={user} isDemo={isDemo} filterYear={filterYear} />;
      case 'settings':
        return <SettingsView user={user} subscription={subscription} isDemo={isDemo} onExitDemo={onExitDemo} />;
      case 'help':
        return <HelpView />;
      default:
        return null;
    }
  };

  return (
    <div className="app-layout">
      {isDemo && (
        <div className="demo-banner">
          <span className="demo-banner-text">Demo Mode - Your data won't be saved</span>
          <button onClick={onExitDemo} className="demo-banner-button">Sign Up to Save</button>
        </div>
      )}

      <MobileHeader
        onMenuClick={() => setIsMobileMenuOpen(true)}
        isDemo={isDemo}
        onExitDemo={onExitDemo}
      />

      <SideNav
        activeView={activeView}
        setActiveView={setActiveView}
        user={user}
        subscription={subscription}
        isDemo={isDemo}
        onExitDemo={onExitDemo}
        onSignOut={onSignOut}
        isMobileOpen={isMobileMenuOpen}
        setIsMobileOpen={setIsMobileMenuOpen}
      />

      <main className="main-content">
        {renderView()}
      </main>
    </div>
  );
}

// PWA Install Prompt Component
function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already running as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true;
    if (isStandalone) return;

    // Check if already dismissed permanently
    const dismissed = localStorage.getItem('creatorcents_pwa_dismissed');
    if (dismissed) return;

    // Check if already shown this session
    const shownThisSession = sessionStorage.getItem('creatorcents_pwa_shown');
    if (shownThisSession) return;

    // Check if mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) return;

    // Detect iOS
    const iOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    setIsIOS(iOS);

    // For Android, listen for beforeinstallprompt
    if (!iOS) {
      const handleBeforeInstall = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowPrompt(true);
        sessionStorage.setItem('creatorcents_pwa_shown', 'true');
      };
      window.addEventListener('beforeinstallprompt', handleBeforeInstall);
      return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    } else {
      // For iOS, show prompt after a short delay
      setTimeout(() => {
        setShowPrompt(true);
        sessionStorage.setItem('creatorcents_pwa_shown', 'true');
      }, 2000);
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android: Use the native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    } else {
      // iOS: Show instructions modal
      setShowModal(true);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('creatorcents_pwa_dismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <>
      <div className="pwa-install-prompt">
        <div className="pwa-install-content">
          <div className="pwa-install-icon">📱</div>
          <div className="pwa-install-text">
            <h4>Install CreatorCents</h4>
            <p>Add to your home screen for quick access</p>
          </div>
          <div className="pwa-install-actions">
            <button onClick={handleInstall} className="pwa-install-btn">
              Install
            </button>
            <button onClick={handleDismiss} className="pwa-install-dismiss">
              Not now
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="pwa-install-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="pwa-install-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{isIOS ? 'Install on iPhone/iPad' : 'Install CreatorCents'}</h3>
            <ol className="pwa-install-steps">
              {isIOS ? (
                <>
                  <li>
                    <span className="step-number">1</span>
                    <span>Tap the <strong>Share</strong> button at the bottom of Safari</span>
                  </li>
                  <li>
                    <span className="step-number">2</span>
                    <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                  </li>
                  <li>
                    <span className="step-number">3</span>
                    <span>Tap <strong>"Add"</strong> in the top right corner</span>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <span className="step-number">1</span>
                    <span>Tap the <strong>menu</strong> (three dots) in your browser</span>
                  </li>
                  <li>
                    <span className="step-number">2</span>
                    <span>Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong></span>
                  </li>
                  <li>
                    <span className="step-number">3</span>
                    <span>Tap <strong>"Install"</strong> to confirm</span>
                  </li>
                </>
              )}
            </ol>
            <button onClick={() => { setShowModal(false); handleDismiss(); }} className="pwa-install-modal-close">
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [currentPage, setCurrentPage] = useState('landing');
  const [isDemo, setIsDemo] = useState(false);
  const [authIsLogin, setAuthIsLogin] = useState(false);
  const previousUserIdRef = useRef(null);
  const subscriptionLoadedRef = useRef(false);

  const checkSubscription = async (userId, force = false) => {
    if (subscriptionLoadedRef.current && !force) return;

    setCheckingSubscription(true);
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription:', error);
      }

      if (data && (data.status === 'active' || data.status === 'trialing')) {
        setSubscription(data);
        subscriptionLoadedRef.current = true;
      } else {
        setSubscription(null);
        subscriptionLoadedRef.current = true;
      }
    } catch (err) {
      console.error('Error checking subscription:', err);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const handleSignOut = async () => {
    console.log('[App] handleSignOut called');
    try {
      // Sign out from Supabase first
      await supabase.auth.signOut();
      console.log('[App] signOut completed');

      // Clear all Supabase-related localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') || key.startsWith('supabase')) {
          localStorage.removeItem(key);
        }
      });

      // Clear sessionStorage
      sessionStorage.clear();

      // Unregister service workers to prevent cached auth state
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
        console.log('[App] Service workers unregistered');
      }

      console.log('[App] Redirecting to home...');
      // Force full page reload
      window.location.href = '/';
    } catch (err) {
      console.error('[App] signOut error:', err);
      // Clear storage and redirect even on error
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      previousUserIdRef.current = currentUser?.id ?? null;

      if (currentUser) {
        checkSubscription(currentUser.id);
      }
      setLoading(false);
    });

    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        const newUser = session?.user ?? null;
        previousUserIdRef.current = newUser?.id ?? null;
        setUser(newUser);
        if (newUser) {
          checkSubscription(newUser.id);
        }
      } else if (event === 'SIGNED_OUT') {
        previousUserIdRef.current = null;
        subscriptionLoadedRef.current = false;
        setUser(null);
        setSubscription(null);
      }
    });

    return () => authSubscription.unsubscribe();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Poll for subscription with retries (used after payment)
    const pollForSubscription = async (userId, maxAttempts = 10) => {
      setProcessingPayment(true);

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        console.log(`Checking for subscription (attempt ${attempt}/${maxAttempts})...`);

        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (data && (data.status === 'active' || data.status === 'trialing')) {
          console.log('Subscription found:', data.plan);
          setSubscription(data);
          subscriptionLoadedRef.current = true;
          setProcessingPayment(false);
          return true;
        }

        // Wait 2 seconds before next attempt
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      console.log('Subscription not found after polling, refreshing...');
      setProcessingPayment(false);
      checkSubscription(userId, true);
      return false;
    };

    // Handle return from successful checkout
    if (urlParams.get('success') === 'true' && user) {
      window.history.replaceState({}, '', window.location.pathname);
      pollForSubscription(user.id);
    }

    // Handle return from Stripe Portal (plan change, cancellation, etc.)
    if (urlParams.get('portal') === 'true' && user) {
      window.history.replaceState({}, '', window.location.pathname);
      // Force refresh subscription after returning from portal
      setTimeout(() => {
        checkSubscription(user.id, true);
      }, 1000);
    }

    // Handle return from Pro upgrade
    if (urlParams.get('upgraded') === 'true' && user) {
      window.history.replaceState({}, '', window.location.pathname);
      pollForSubscription(user.id);
    }
  }, [user]);

  // Determine which content to render
  const renderContent = () => {
    if (loading) {
      return (
        <div className="app loading-screen">
          <div className="loading-text">Loading...</div>
        </div>
      );
    }

    // Demo mode
    if (isDemo) {
      return (
        <AppLayout
          isDemo={true}
          onExitDemo={() => setIsDemo(false)}
          onSignOut={() => setIsDemo(false)}
        />
      );
    }

    // Not logged in
    if (!user) {
      if (currentPage === 'privacy') {
        return <PrivacyPolicy onBack={() => setCurrentPage('landing')} />;
      }
      if (currentPage === 'terms') {
        return <TermsOfService onBack={() => setCurrentPage('landing')} />;
      }
      if (currentPage === 'auth') {
        return <Auth onBack={() => setCurrentPage('landing')} defaultIsLogin={authIsLogin} />;
      }
      return (
        <LandingPage
          onGetStarted={() => { setAuthIsLogin(false); setCurrentPage('auth'); }}
          onLogin={() => { setAuthIsLogin(true); setCurrentPage('auth'); }}
          onTryDemo={() => setIsDemo(true)}
          onPrivacy={() => setCurrentPage('privacy')}
          onTerms={() => setCurrentPage('terms')}
        />
      );
    }

    // Checking subscription
    if (checkingSubscription && !subscriptionLoadedRef.current) {
      return (
        <div className="app loading-screen">
          <div className="loading-text">Checking subscription...</div>
        </div>
      );
    }

    // Processing payment - show loading while waiting for webhook
    if (processingPayment) {
      return (
        <div className="app loading-screen">
          <div className="loading-text">Processing your payment...</div>
          <div className="loading-subtext" style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>
            This may take a few seconds
          </div>
        </div>
      );
    }

    // No subscription - show paywall
    if (!subscription) {
      return <Paywall user={user} onSubscribed={() => checkSubscription(user.id, true)} />;
    }

    // Has subscription - show main app
    return (
      <>
        <AppLayout
          user={user}
          subscription={subscription}
          isDemo={false}
          onSignOut={handleSignOut}
        />
        <PWAInstallPrompt />
      </>
    );
  };

  return (
    <>
      {renderContent()}
      <SpeedInsights />
      <Analytics />
    </>
  );
}

export default App;
